import React, { createContext, useState, useContext, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box } from '@mui/material';
import { useLanguage } from './LanguageContext';
// mockData imports removed — all state is now loaded from DB, never from local mock data
import { v4 as uuidv4 } from 'uuid';
import { playSuccessSound, playBillRequestSound, playBillSettledSound } from '../utils/soundHelper';
import API_BASE_URL from '../config/api';

const AppContext = createContext();

const safeJsonParse = (str, fallback) => {
    if (!str || str === 'undefined' || str === 'null') return fallback;
    try {
        const val = JSON.parse(str);
        return val !== null && val !== undefined ? val : fallback;
    } catch (e) {
        return fallback;
    }
};

const getOfflineCacheKey = (baseKey) => {
    const tenant = localStorage.getItem('dinexa_active_tenant_subdomain') || 'demo';
    const userJson = sessionStorage.getItem('react_pos_current_user') || localStorage.getItem('react_pos_current_user');
    let branch = localStorage.getItem('dinexa_active_branch_id') || 'main';
    if (userJson) {
        try {
            const user = safeJsonParse(userJson, null);
            if (user && user.branch_id) branch = user.branch_id;
        } catch(e){}
    }
    return `${baseKey}_${tenant}_${branch}`;
};

/**
 * Prunes an orders array for offline localStorage caching.
 * Keeps:
 * - ALL active/unsettled/cooking/occupied-table orders
 * - Top 100 most recent settled/completed orders
 * Prevents localStorage QuotaExceededError while preserving all active operations offline.
 */
export const pruneOrdersForStorage = (ordersList) => {
    if (!Array.isArray(ordersList)) return [];
    
    const activeOrders = [];
    const settledOrders = [];
    const terminalStatuses = ['settled', 'paid', 'cancelled', 'served', 'completed'];

    for (let i = 0; i < ordersList.length; i++) {
        const order = ordersList[i];
        if (!order) continue;
        const st = String(order.status || '').toLowerCase();
        if (!terminalStatuses.includes(st)) {
            activeOrders.push(order);
        } else {
            settledOrders.push(order);
        }
    }

    if (settledOrders.length > 100) {
        settledOrders.sort((a, b) => {
            const timeA = new Date(a.created_at || a.timestamp || 0).getTime();
            const timeB = new Date(b.created_at || b.timestamp || 0).getTime();
            return timeB - timeA;
        });
        settledOrders.length = 100;
    }

    return [...activeOrders, ...settledOrders];
};

/**
 * Robust, exception-safe localStorage setter that handles QuotaExceededError.
 */
export const safeSetLocalStorage = (key, value) => {
    try {
        const serialized = typeof value === 'string' ? value : JSON.stringify(value);
        localStorage.setItem(key, serialized);
    } catch (e) {
        console.warn(`[safeSetLocalStorage] Failed to write key "${key}":`, e);
        if (e.name === 'QuotaExceededError' || e.code === 22 || e.code === 1014 || (e.message && e.message.toLowerCase().includes('quota'))) {
            try {
                // If the key is orders, prune to active-only orders
                if (key.includes('react_pos_orders')) {
                    const raw = typeof value === 'string' ? safeJsonParse(value, []) : value;
                    if (Array.isArray(raw)) {
                        const emergencyPruned = raw.filter(o => !['settled', 'paid', 'cancelled', 'served'].includes(String(o.status || '').toLowerCase())).slice(0, 30);
                        localStorage.setItem(key, JSON.stringify(emergencyPruned));
                        console.log(`[safeSetLocalStorage] Recovered from quota error by caching ${emergencyPruned.length} active orders.`);
                        return;
                    }
                }
                // Clean up non-essential keys
                const obsoleteKeys = ['react_pos_orders', 'qr_orders', 'react_pos_inventory'];
                for (const ok of obsoleteKeys) {
                    if (ok !== key) {
                        try { localStorage.removeItem(ok); } catch (ign) {}
                    }
                }
            } catch (recoveryErr) {
                console.error("[safeSetLocalStorage] Emergency recovery failed:", recoveryErr);
            }
        }
    }
};

/**
 * Helper to save orders to offline cache:
 * 1. Prunes to active + recent settled orders
 * 2. Writes using safeSetLocalStorage
 */
export const saveOrdersOfflineCache = (key, ordersList) => {
    const pruned = pruneOrdersForStorage(ordersList);
    safeSetLocalStorage(key, pruned);
};


export const generateUniqueOrderId = (branchId = null) => {
    let bId = branchId;
    if (!bId && typeof window !== 'undefined') {
        bId = localStorage.getItem('dinexa_active_branch_id');
        if (!bId) {
            try {
                const u = sessionStorage.getItem('react_pos_current_user') || localStorage.getItem('react_pos_current_user');
                if (u) bId = JSON.parse(u)?.branch_id;
            } catch(e) {}
        }
    }
    bId = parseInt(bId) || 1;
    const now = new Date();
    const timePart = `${String(now.getDate()).padStart(2, '0')}${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}${String(now.getSeconds()).padStart(2, '0')}`;
    const rand = Math.floor(100 + Math.random() * 900);
    return `ORD-B${bId}-${timePart}-${rand}`;
};

export const generateUniqueKOTId = (branchId = null) => {
    let bId = branchId;
    if (!bId && typeof window !== 'undefined') {
        bId = localStorage.getItem('dinexa_active_branch_id');
        if (!bId) {
            try {
                const u = sessionStorage.getItem('react_pos_current_user') || localStorage.getItem('react_pos_current_user');
                if (u) bId = JSON.parse(u)?.branch_id;
            } catch(e) {}
        }
    }
    bId = parseInt(bId) || 1;
    const rand = Math.floor(100 + Math.random() * 900);
    return `KOT-B${bId}-${rand}`;
};

export const AppProvider = ({ children }) => {
    const { t } = useLanguage();

    // Bulletproof 4-Layer Persistent Branch Resolver (Zero-Friction Lock)
    const getActiveBranchId = () => {
        // 1. Direct from in-memory currentUser state
        if (currentUser && currentUser.branch_id) {
            return parseInt(currentUser.branch_id);
        }
        // 2. From sessionStorage / localStorage (logged-in user profile)
        try {
            const u = sessionStorage.getItem('react_pos_current_user') || localStorage.getItem('react_pos_current_user');
            if (u) {
                const parsed = safeJsonParse(u, null);
                if (parsed && parsed.branch_id) return parseInt(parsed.branch_id);
            }
        } catch (e) {}
        // 3. Match from active user's name against persistent branches list
        try {
            const rawBranches = localStorage.getItem('react_pos_branches');
            const savedUser = sessionStorage.getItem('react_pos_current_user') || localStorage.getItem('react_pos_current_user');
            if (rawBranches && savedUser) {
                const bList = safeJsonParse(rawBranches, []);
                const uObj = safeJsonParse(savedUser, {});
                if (Array.isArray(bList) && uObj?.name) {
                    const uName = uObj.name.toLowerCase();
                    const parts = uName.split(' ').filter(p => p.length >= 3);
                    const matched = bList.find(b => {
                        const bName = (b.name || '').toLowerCase();
                        return parts.some(p => bName.includes(p));
                    });
                    if (matched) return parseInt(matched.id);
                }
            }
        } catch (e) {}
        // 4. From localStorage persistent branch lock
        const localBranch = localStorage.getItem('dinexa_active_branch_id');
        if (localBranch && localBranch !== 'null' && localBranch !== 'undefined' && localBranch !== '' && localBranch !== '0') {
            return parseInt(localBranch);
        }
        // 5. Default fallback to first branch from saved branches
        try {
            const rawBranches = localStorage.getItem('react_pos_branches');
            if (rawBranches) {
                const bList = safeJsonParse(rawBranches, []);
                if (Array.isArray(bList) && bList.length > 0 && bList[0].id) {
                    return parseInt(bList[0].id);
                }
            }
        } catch (e) {}
        return 1;
    };

    const getTenantFromUrl = () => {
        // 1. Check URL parameters on ANY domain (?store=xyz or ?tenant=xyz or ?subdomain=xyz)
        const params = new URLSearchParams(window.location.search);
        const queryTenant = params.get('store') || params.get('tenant') || params.get('subdomain');
        if (queryTenant && queryTenant.trim() !== '') {
            const clean = queryTenant.toLowerCase().trim();
            localStorage.setItem('dinexa_active_tenant_subdomain', clean);
            return clean;
        }

        // 2. Subdomain check (e.g. dattkrupabhojnalay.dinexapos.online)
        const hostname = window.location.hostname;
        const parts = hostname.split('.');
        if (parts.length > 2) {
            const sub = parts[0].toLowerCase();
            const reserved = ['www', 'app', 'landlord', 'admin', 'dinexapos', 'pos'];
            if (!reserved.includes(sub)) {
                localStorage.setItem('dinexa_active_tenant_subdomain', sub);
                return sub;
            }
        }

        // 3. LocalStorage fallback
        return localStorage.getItem('dinexa_active_tenant_subdomain') || '';
    };

    const [tenantSubdomain, setTenantSubdomainState] = useState(getTenantFromUrl());
    const [isLoggedIn, setIsLoggedIn] = useState(() => {
        return localStorage.getItem('dinexa_is_logged_in') === 'true' || sessionStorage.getItem('dinexa_is_logged_in') === 'true';
    });

    const setTenantSubdomain = (sub) => {
        const clean = (sub || '').toLowerCase().trim();
        localStorage.setItem('dinexa_active_tenant_subdomain', clean);
        setTenantSubdomainState(clean);
    };

    // Offline / Online Status & Queue State
    const [isOnline, setIsOnline] = useState(navigator.onLine);
    const [offlineOrdersQueue, setOfflineOrdersQueue] = useState(() => {
        const saved = localStorage.getItem('dinexa_offline_orders_queue');
        const parsed = safeJsonParse(saved, []);
        return Array.isArray(parsed) ? parsed : [];
    });
    const [isSubscriptionBlocked, setIsSubscriptionBlocked] = useState(false);
    const [subscriptionLockReason, setSubscriptionLockReason] = useState('');

    const logout = () => {
        sessionStorage.removeItem('dinexa_is_logged_in');
        localStorage.removeItem('dinexa_is_logged_in');
        sessionStorage.removeItem('react_pos_current_user');
        localStorage.removeItem('react_pos_current_user');
        sessionStorage.removeItem('react_pos_current_role');
        localStorage.removeItem('react_pos_current_role');
        sessionStorage.removeItem('react_pos_allowed_menus');
        localStorage.removeItem('react_pos_allowed_menus');
        sessionStorage.removeItem('react_pos_locked_actions');
        localStorage.removeItem('react_pos_locked_actions');
        localStorage.removeItem('dinexa_active_view');
        sessionStorage.removeItem('dinexa_active_view');
        localStorage.removeItem('dinexa_app_mode');
        // Clear all DB-sourced caches so stale data does not appear on next login
        localStorage.removeItem('react_pos_orders');
        localStorage.removeItem(getOfflineCacheKey('react_pos_orders'));
        localStorage.removeItem('react_pos_tables');
        localStorage.removeItem(getOfflineCacheKey('react_pos_tables'));
        localStorage.removeItem('react_pos_sections');
        localStorage.removeItem('react_pos_inventory');
        localStorage.removeItem('react_pos_online_orders');
        setIsLoggedIn(false);
        setTenantSubdomain('');
        setCurrentUser(null);
        setCurrentRole('owner');
        setAllowedMenus(null);
        setLockedActions([]);
        setOrders([]);
        setTables([{ id: 'counter', number: 'Counter', status: 'vacant', activeOrderId: null }]);
        setSections([]);
        setInventory([]);
        setOnlineOrders([]);
    };

    // Current Active Screen View (Persistent across browser reloads & multi-refreshes)
    const [activeView, setActiveViewState] = useState(() => {
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('app') === 'biz') return 'biz';
        if (urlParams.get('app') === 'pos') return 'tables';

        const savedView = localStorage.getItem('dinexa_active_view') || sessionStorage.getItem('dinexa_active_view');
        if (savedView && savedView !== 'appLauncher') {
            return savedView;
        }

        const rawRole = sessionStorage.getItem('react_pos_current_role') || localStorage.getItem('react_pos_current_role');
        const savedRole = safeJsonParse(rawRole, rawRole || 'owner');

        const savedMenus = sessionStorage.getItem('react_pos_allowed_menus') || localStorage.getItem('react_pos_allowed_menus');
        if (savedMenus) {
            try {
                const parsed = safeJsonParse(savedMenus, null);
                if (Array.isArray(parsed) && parsed.length > 0) {
                    if (parsed.includes('tables')) return 'tables';
                    if (parsed.includes('pos')) return 'pos';
                    if (parsed.includes('quickPos')) return 'quickPos';
                    if (parsed.includes('kds')) return 'kds';
                    if (parsed.includes('captain')) return 'captain';
                    return parsed[0];
                }
            } catch (e) {}
        }
        if (savedRole === 'captain') return 'captain';
        if (savedRole === 'kitchen_staff') return 'kds';
        if (savedRole === 'billing_staff') return 'tables';
        return 'tables';
    });

    const setActiveView = (view) => {
        if (view && view !== 'appLauncher') {
            localStorage.setItem('dinexa_active_view', view);
            sessionStorage.setItem('dinexa_active_view', view);
        }
        setActiveViewState(view);
    };
    
    // Core State — always loaded from DB on login. localStorage is only a short-term cache.
    const [tables, setTables] = useState(() => {
        const saved = localStorage.getItem(getOfflineCacheKey('react_pos_tables'));
        const raw = safeJsonParse(saved, []);
        const parsed = Array.isArray(raw) ? raw.filter(Boolean) : [];
        const normalized = parsed.map(t => ({ ...t, display_order: parseInt(t.display_order) || 0 }));
        if (!normalized.some(t => t && t.id === 'counter')) {
            normalized.push({ id: 'counter', number: 'Counter', status: 'vacant', activeOrderId: null, display_order: 0 });
        }
        return normalized;
    });

    // Multi-table active cart preservation map ({ tableId: [items] })
    const [tableCarts, setTableCarts] = useState({});

    const [orders, setOrders] = useState(() => {
        const cacheKey = getOfflineCacheKey('react_pos_orders');
        const saved = localStorage.getItem(cacheKey);
        const parsed = safeJsonParse(saved, []);
        const list = Array.isArray(parsed) ? parsed.filter(Boolean) : [];
        if (list.length > 150) {
            try {
                const pruned = pruneOrdersForStorage(list);
                safeSetLocalStorage(cacheKey, pruned);
            } catch(e) {}
        }
        return list;
    });

    const [sections, setSections] = useState(() => {
        const saved = localStorage.getItem('react_pos_sections');
        const parsed = safeJsonParse(saved, []);
        return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
    });

    const [onlineOrders, setOnlineOrders] = useState(() => {
        const saved = localStorage.getItem('react_pos_online_orders');
        const parsed = safeJsonParse(saved, []);
        return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
    });

    const [inventory, setInventory] = useState(() => {
        const saved = localStorage.getItem('react_pos_inventory');
        const parsed = safeJsonParse(saved, []);
        return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
    });

    // Inward Ledger (Purchase History)
    const [inwardLedger, setInwardLedger] = useState(() => {
        const saved = localStorage.getItem('react_pos_inward_ledger');
        const parsed = safeJsonParse(saved, []);
        return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
    });

    // Outward Ledger (Manual Wastage / Spoilage / Staff Meal)
    const [outwardLedger, setOutwardLedger] = useState(() => {
        const saved = localStorage.getItem('react_pos_outward_ledger');
        const parsed = safeJsonParse(saved, []);
        return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
    });

    // Dynamic Menu Items & Recipes States — loaded from DB on login, no mock fallback
    const [menuItems, setMenuItems] = useState(() => {
        const saved = localStorage.getItem(getOfflineCacheKey('react_pos_menu_items'));
        const raw = safeJsonParse(saved, []);
        let list = Array.isArray(raw) ? raw.filter(Boolean) : [];
        if (Array.isArray(list)) {
            list.sort((a, b) => (parseInt(a.display_order !== undefined ? a.display_order : a.displayOrder) || 0) - (parseInt(b.display_order !== undefined ? b.display_order : b.displayOrder) || 0));
        }
        return list;
    });

    const [categories, setCategories] = useState(() => {
        const saved = localStorage.getItem(getOfflineCacheKey('react_pos_categories'));
        const parsed = safeJsonParse(saved, []);
        return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
    });

    const [recipes, setRecipes] = useState(() => {
        const saved = localStorage.getItem('react_pos_recipes');
        const parsed = safeJsonParse(saved, []);
        return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
    });

    // Subscription modules: defaults to all active for demo
    const [subscribedModules, setSubscribedModules] = useState(() => {
        const saved = localStorage.getItem('react_pos_subscribed_modules');
        const parsed = safeJsonParse(saved, ['pos', 'kds', 'captain', 'inventory', 'accounting', 'compliance', 'zomato_swiggy']);
        return Array.isArray(parsed) ? parsed : ['pos', 'kds', 'captain', 'inventory', 'accounting', 'compliance', 'zomato_swiggy'];
    });

    // Role-based Access tiers: 'staff', 'manager', 'owner'
    const [currentRole, setCurrentRole] = useState(() => {
        const saved = sessionStorage.getItem('react_pos_current_role') || localStorage.getItem('react_pos_current_role');
        return safeJsonParse(saved, 'owner');
    });

    // Current Logged-in Tenant User Details
    const [currentUser, setCurrentUser] = useState(() => {
        const saved = sessionStorage.getItem('react_pos_current_user') || localStorage.getItem('react_pos_current_user');
        return safeJsonParse(saved, null);
    });

    // Allowed menus for this tenant (null = no restriction, array = restricted list)
    const [allowedMenus, setAllowedMenus] = useState(() => {
        const saved = sessionStorage.getItem('react_pos_allowed_menus') || localStorage.getItem('react_pos_allowed_menus');
        return safeJsonParse(saved, null);
    });

    // Locked actions for this staff member (empty array = no locks)
    const [lockedActions, setLockedActions] = useState(() => {
        const saved = sessionStorage.getItem('react_pos_locked_actions') || localStorage.getItem('react_pos_locked_actions');
        const parsed = safeJsonParse(saved, []);
        return Array.isArray(parsed) ? parsed : [];
    });

    // Realtime Cloud Sync Toggle (Default false for 0% CPU lag on 2GB RAM single-counter tablets)
    const [cloudSyncEnabled, setCloudSyncEnabled] = useState(() => {
        const saved = localStorage.getItem('dinexa_realtime_sync');
        return safeJsonParse(saved, false);
    });

    const toggleCloudSync = (val) => {
        const nextVal = typeof val === 'boolean' ? val : !cloudSyncEnabled;
        setCloudSyncEnabled(nextVal);
        localStorage.setItem('dinexa_realtime_sync', JSON.stringify(nextVal));
        showToast(nextVal ? '⚡ क्लाऊड सिंक ऑन केले!' : '💤 क्लाऊड सिंक ऑफ केले (फास्ट टॅब मोड)');
    };

    useEffect(() => {
        if (currentUser) {
            sessionStorage.setItem('react_pos_current_user', JSON.stringify(currentUser));
            localStorage.setItem('react_pos_current_user', JSON.stringify(currentUser));
        }
    }, [currentUser]);

    const [receiptTemplate, setReceiptTemplate] = useState(() => {
        const saved = localStorage.getItem('react_pos_receipt_template') || localStorage.getItem('dinexa_receipt_template');
        try {
            if (saved) return (saved.startsWith('"') && saved.endsWith('"')) ? JSON.parse(saved) : saved;
        } catch(e) {}
        return 'coupon1';
    });

    const [printFeedLines, setPrintFeedLines] = useState(() => {
        const saved = localStorage.getItem('dinexa_print_feed_lines');
        if (saved !== null && !isNaN(parseInt(saved, 10))) {
            return parseInt(saved, 10);
        }
        return 3;
    });

    useEffect(() => {
        if (currentUser && currentUser.receipt_template) {
            setReceiptTemplate(currentUser.receipt_template);
        }
        if (currentUser && currentUser.print_feed_lines !== undefined && currentUser.print_feed_lines !== null) {
            setPrintFeedLines(parseInt(currentUser.print_feed_lines, 10));
        }
    }, [currentUser]);

    useEffect(() => {
        localStorage.setItem('react_pos_receipt_template', JSON.stringify(receiptTemplate));
        localStorage.setItem('dinexa_receipt_template', receiptTemplate);
    }, [receiptTemplate]);

    useEffect(() => {
        localStorage.setItem('dinexa_print_feed_lines', printFeedLines.toString());
    }, [printFeedLines]);

    // POS Menu Card Layout & Dish Image Visibility (Default: Option 3 - Compact Layout 2: Sober Slate)
    const [posCardLayout, setPosCardLayoutState] = useState(() => {
        const isExplicit = localStorage.getItem('dinexa_pos_card_layout_explicit') === 'true';
        const saved = localStorage.getItem('dinexa_pos_card_layout');
        if (isExplicit && saved) {
            return saved;
        }
        return 'compact2';
    });

    const setPosCardLayout = (layout) => {
        setPosCardLayoutState(layout);
        localStorage.setItem('dinexa_pos_card_layout', layout);
        localStorage.setItem('dinexa_pos_card_layout_explicit', 'true');
        setShowMenuImages(layout === 'image');
    };

    const [showMenuImages, setShowMenuImagesState] = useState(() => {
        const saved = localStorage.getItem('dinexa_show_menu_images');
        if (saved !== null) return saved === 'true';
        return false;
    });

    const setShowMenuImages = (val) => {
        setShowMenuImagesState(val);
        localStorage.setItem('dinexa_show_menu_images', val ? 'true' : 'false');
    };

    // Bulletproof Tenant Subdomain Resolver Helper
    const getActiveSubdomain = () => {
        if (tenantSubdomain && tenantSubdomain !== 'demo') return tenantSubdomain;
        if (currentUser && currentUser.subdomain) return currentUser.subdomain;
        const stored = localStorage.getItem('dinexa_active_tenant_subdomain');
        if (stored && stored !== 'demo') return stored;

        const hostname = window.location.hostname;
        const parts = hostname.split('.');
        if (parts.length >= 3 && parts[0] !== 'www' && parts[0] !== 'localhost' && parts[0] !== '127') {
            return parts[0];
        }
        return stored || 'demo';
    };

    // Operator Offline Queue Confirmation Modal State
    const [offlineConfirmModal, setOfflineConfirmModal] = useState({
        open: false,
        payload: null,
        errorMsg: '',
        type: ''
    });

    const saveToOfflineQueue = (payload) => {
        const currentQueue = JSON.parse(localStorage.getItem('dinexa_offline_orders_queue') || '[]');
        const newItem = {
            id: payload.id || payload.order_id || `OFF-${Date.now()}`,
            timestamp: new Date().toISOString(),
            ...payload
        };
        const updated = [...currentQueue, newItem];
        localStorage.setItem('dinexa_offline_orders_queue', JSON.stringify(updated));
        setOfflineOrdersQueue(updated);
    };

    const removeOfflineQueueItem = (orderId) => {
        const queueStr = localStorage.getItem('dinexa_offline_orders_queue');
        if (!queueStr) return;
        try {
            const queue = JSON.parse(queueStr);
            if (Array.isArray(queue)) {
                const filtered = queue.filter(item => {
                    const itemId = item.id || item.order_id || (item.payload && (item.payload.id || item.payload.order_id));
                    return String(itemId) !== String(orderId);
                });
                localStorage.setItem('dinexa_offline_orders_queue', JSON.stringify(filtered));
                setOfflineOrdersQueue(filtered);
            }
        } catch (e) {
            console.error("Failed to remove offline queue item:", e);
        }
    };

    const clearOfflineQueue = () => {
        localStorage.setItem('dinexa_offline_orders_queue', JSON.stringify([]));
        setOfflineOrdersQueue([]);
        showToast('✅ सर्व ऑफलाइन Queue साफ केली!');
    };

    // Permanently delete an order from database and local state (Owner only)
    const deleteOrder = async (orderId) => {
        if (!orderId) return { success: false, error: 'Order ID is required' };

        const activeSub = getActiveSubdomain();
        const activeBranchId = parseInt(getActiveBranchId()) || 1;

        try {
            if (navigator.onLine && activeSub && activeSub !== 'demo') {
                const res = await fetch(`${API_BASE_URL}/tenant/orders/${encodeURIComponent(orderId)}?tenant=${encodeURIComponent(activeSub)}&branch_id=${activeBranchId}`, {
                    method: 'DELETE',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Tenant-Subdomain': activeSub
                    },
                    body: JSON.stringify({ order_id: orderId, branch_id: activeBranchId })
                });
                const data = await res.json();
                if (!res.ok || !data.success) {
                    throw new Error(data.error || `Server HTTP ${res.status}`);
                }
            }

            // Remove order from local state & cache
            setOrders(prev => {
                const updated = prev.filter(o => String(o.id) !== String(orderId) && String(o.order_id) !== String(orderId));
                saveOrdersOfflineCache(getOfflineCacheKey('react_pos_orders'), updated);
                return updated;
            });

            // If any table was tied to this order, free it up
            setTables(prev => {
                const updated = prev.map(t => {
                    if (String(t.activeOrderId) === String(orderId)) {
                        return { ...t, status: 'vacant', activeOrderId: null };
                    }
                    return t;
                });
                localStorage.setItem(getOfflineCacheKey('react_pos_tables'), JSON.stringify(updated));
                return updated;
            });

            // Also clean up from offline queue if present
            removeOfflineQueueItem(orderId);

            showToast(`✅ बिल ${orderId} कायमचे डिलीट केले!`);
            return { success: true };
        } catch (err) {
            console.error("Failed to delete order:", err);
            showToast(`❌ बिल डिलीट करण्यात त्रुटी: ${err.message}`);
            return { success: false, error: err.message };
        }
    };

    const handleConfirmOfflineSave = () => {
        if (offlineConfirmModal.payload) {
            saveToOfflineQueue(offlineConfirmModal.payload);
            showToast('📦 ऑर्डर ऑफलाईन मेमरी (Offline Queue) मध्ये सेव्ह झाली!');
        }
        setOfflineConfirmModal({ open: false, payload: null, errorMsg: '', type: '' });
    };

    const handleCancelOfflineSave = () => {
        showToast('ℹ️ ऑफलाईन सेव्ह रद्द केले. नेटवर्क तपासून पुन्हा प्रयत्न करा.');
        setOfflineConfirmModal({ open: false, payload: null, errorMsg: '', type: '' });
    };

    // Bulletproof Normalization Helper for Orders (handles snake_case & camelCase seamlessly)
    const normalizeOrders = (rawOrders) => {
        if (!Array.isArray(rawOrders)) return [];
        return rawOrders.map(o => {
            const bizDate = o.business_date || o.businessDate || (o.created_at ? String(o.created_at).substring(0, 10) : (o.createdAt ? String(o.createdAt).substring(0, 10) : null));
            const oSource = o.order_source || o.orderSource || (o.type && o.type.toLowerCase().includes('zomato') ? 'zomato' : (o.type && o.type.toLowerCase().includes('swiggy') ? 'swiggy' : (o.type && o.type.toLowerCase().includes('delivery') ? 'delivery' : (o.type && o.type.toLowerCase().includes('takeaway') ? 'takeaway' : 'dine_in'))));
            return {
                ...o,
                id: o.id || o.order_id,
                order_id: o.id || o.order_id,
                branch_id: o.branch_id ? parseInt(o.branch_id) : 1,
                business_date: bizDate,
                businessDate: bizDate,
                tableId: o.table_id || o.tableId || 'counter',
                table_id: o.table_id || o.tableId || 'counter',
                type: o.type || 'Dine In',
                order_source: oSource,
                orderSource: oSource,
                waiterName: o.waiter_name || o.waiterName || 'Staff',
                waiter_name: o.waiter_name || o.waiterName || 'Staff',
                paymentMethod: o.payment_method || o.paymentMethod || 'cash',
                payment_method: o.payment_method || o.paymentMethod || 'cash',
                createdAt: o.created_at || o.createdAt,
                created_at: o.created_at || o.createdAt,
                subtotal: Number(o.subtotal) || 0,
                tax: Number(o.tax) || 0,
                discount: Number(o.discount) || 0,
                discount_reason: o.discount_reason || o.discountReason || '',
                serviceCharge: Number(o.service_charge || o.serviceCharge) || 0,
                service_charge: Number(o.service_charge || o.serviceCharge) || 0,
                total: Number(o.total) || 0,
                status: o.status || 'cooking',
                items: Array.isArray(o.items) ? o.items.map(it => ({
                    ...it,
                    id: it.menu_item_id || it.id,
                    menu_item_id: it.menu_item_id || it.id,
                    quantity: Number(it.quantity) || 1,
                    price: Number(it.price) || 0,
                    name: it.name || 'General Dish',
                    name_mr: it.name_mr || it.nameMr || it.name || '',
                    name_hi: it.name_hi || it.nameHi || it.name || ''
                })) : []
            };
        });
    };

    // 🛡️ Bulletproof Non-Destructive Orders Merge
    // Guarantees that any local active order (cooking, preparing, pending, billed) or newly created KOT/Zomato/Swiggy/Counter order
    // is NEVER erased by a server sync response if the server DB had a momentary connection hiccup or hasn't saved it yet!
    const mergeOrdersSafely = (serverOrders, prevOrders) => {
        const normalized = normalizeOrders(serverOrders || []);
        const queueStr = localStorage.getItem('dinexa_offline_orders_queue');
        const offlineQueue = queueStr ? JSON.parse(queueStr) : [];
        const offlineSettledIds = new Set(offlineQueue.filter(q => q.status === 'served' || q.payment_method).map(q => String(q.id || q.order_id)));
        const locallyServedIds = new Set((prevOrders || []).filter(o => o.status === 'served').map(o => String(o.id || o.order_id)));

        const serverOrderMap = new Map();
        normalized.forEach(so => {
            const sId = String(so.id || so.order_id);
            if (offlineSettledIds.has(sId) || locallyServedIds.has(sId)) {
                const localMatch = (prevOrders || []).find(o => String(o.id || o.order_id) === sId);
                serverOrderMap.set(sId, {
                    ...so,
                    status: 'served',
                    paymentMethod: localMatch?.paymentMethod || so.paymentMethod || 'cash',
                    payment_method: localMatch?.payment_method || so.payment_method || 'cash'
                });
            } else {
                // If local order has more recent KOT items, preserve them
                const localMatch = (prevOrders || []).find(o => String(o.id || o.order_id) === sId);
                if (localMatch && Array.isArray(localMatch.kots) && localMatch.kots.length > (so.kots?.length || 0)) {
                    serverOrderMap.set(sId, {
                        ...so,
                        kots: localMatch.kots,
                        items: (localMatch.items && localMatch.items.length >= (so.items?.length || 0)) ? localMatch.items : so.items,
                        total: localMatch.total || so.total
                    });
                } else {
                    serverOrderMap.set(sId, so);
                }
            }
        });

        // 🛡️ CRITICAL: Retain any local ACTIVE order (preparing/cooking/pending/billed) that is NOT yet in the server response
        const retainedLocalActive = (prevOrders || []).filter(lo => {
            const lId = String(lo.id || lo.order_id);
            if (serverOrderMap.has(lId)) return false;
            const st = String(lo.status || '').toLowerCase();
            return !['served', 'cancelled', 'paid', 'settled'].includes(st);
        });

        // Retain any offline queue item not yet in server list or local active
        const retainedOfflineQueue = offlineQueue
            .filter(qo => {
                const qId = String(qo.id || qo.order_id);
                return !serverOrderMap.has(qId) && !retainedLocalActive.some(lo => String(lo.id || lo.order_id) === qId);
            })
            .map(qo => normalizeOrders([qo])[0])
            .filter(Boolean);

        return [...Array.from(serverOrderMap.values()), ...retainedLocalActive, ...retainedOfflineQueue];
    };

    // 🛡️ Bulletproof Non-Destructive Tables Merge
    const mergeTablesSafely = (serverTables, prevTables) => {
        if (!Array.isArray(serverTables)) return prevTables || [];
        const activeBranchId = parseInt(getActiveBranchId()) || 1;
        
        const mapped = serverTables.map(t => {
            const tId = String(t.id || t.table_number || t.number);
            const prevTable = (prevTables || []).find(pt => String(pt.id) === tId || String(pt.number) === String(t.number || t.table_number));
            
            let status = t.status || 'vacant';
            let activeOrderId = t.active_order_id || t.activeOrderId || null;

            // 🛡️ Safe Table Status Sync
            // Check if there is an active unserved order or local draft in progress
            const hasLocalActiveOrder = (orders || []).some(o => 
                !['served', 'cancelled'].includes(String(o.status || '').toLowerCase()) && 
                (String(o.table_id || o.tableId) === tId || (t.number && String(o.table_id || o.tableId) === String(t.number)))
            );
            const hasLocalDraft = Array.isArray(tableCarts && tableCarts[tId]) && tableCarts[tId].length > 0;

            // Check if offline queue has a settled order for this table
            const queueStr = localStorage.getItem('dinexa_offline_orders_queue');
            let isSettledInOfflineQueue = false;
            try {
                const qList = queueStr ? JSON.parse(queueStr) : [];
                isSettledInOfflineQueue = Array.isArray(qList) && qList.some(q => 
                    q.status === 'served' && 
                    (String(q.table_id) === tId || (t.number && String(q.table_id) === String(t.number)))
                );
            } catch (e) {}

            if (!isSettledInOfflineQueue && (hasLocalActiveOrder || hasLocalDraft)) {
                // Table has an active unserved order or draft cart locally -> preserve occupied
                if (status === 'vacant' && !activeOrderId) {
                    status = hasLocalDraft ? 'occupied' : (prevTable?.status || 'occupied');
                    activeOrderId = prevTable?.activeOrderId || activeOrderId || null;
                }
            } else if (isSettledInOfflineQueue || (!hasLocalActiveOrder && !hasLocalDraft)) {
                // Table has NO active order and NO draft (e.g. order settled offline or finished) -> must be vacant!
                status = 'vacant';
                activeOrderId = null;
            }

            return {
                id: tId,
                branch_id: t.branch_id || activeBranchId,
                number: String(t.number || t.table_number || tId),
                section_id: t.section_id || null,
                status: status,
                activeOrderId: activeOrderId,
                display_order: parseInt(t.display_order) || 0,
                billRequested: status === 'billed' || (prevTable && prevTable.billRequested)
            };
        });

        if (!mapped.some(t => t.id === 'counter')) {
            mapped.push({ id: 'counter', number: 'Counter', status: 'vacant', activeOrderId: null });
        }
        return mapped;
    };

    // Robust Database Sync for all Tenant Data
    const syncTenantDataFromDatabase = async () => {
        const activeSub = getActiveSubdomain();
        if (!activeSub || activeSub === 'demo') return;

        // Cleanup stale mock keys
        const MOCK_LEDGER_IDS = ['pur-1', 'pur-2', 'wast-1', 'wast-2', 'acc-1', 'acc-2'];
        ['react_pos_inward_ledger', 'react_pos_outward_ledger', 'react_pos_accounting_ledger'].forEach(key => {
            const raw = localStorage.getItem(key);
            if (raw) {
                try {
                    const parsed = JSON.parse(raw);
                    const cleaned = parsed.filter(e => !MOCK_LEDGER_IDS.includes(e.id));
                    if (cleaned.length !== parsed.length) {
                        localStorage.setItem(key, JSON.stringify(cleaned));
                        if (key === 'react_pos_inward_ledger') setInwardLedger(cleaned);
                        if (key === 'react_pos_outward_ledger') setOutwardLedger(cleaned);
                        if (key === 'react_pos_accounting_ledger') setAccountingLedger(cleaned);
                    }
                } catch (e) {}
            }
        });

        const isOwner = (currentUser?.role === 'owner' || currentUser?.is_owner);
        const activeBranchId = getActiveBranchId();
        const branchQ = (!isOwner && activeBranchId) ? `&branch_id=${activeBranchId}` : '';
        const tableBranchQ = activeBranchId ? `?branch_id=${activeBranchId}` : '';

        try {
            // Sync Orders from DB (Owner gets all branch orders for Reports/Analytics, Operators get their active branch)
            const ordersRes = await fetch(`${API_BASE_URL}/tenant/orders?tenant=${encodeURIComponent(activeSub)}${branchQ}`, {
                headers: { 
                    'X-Tenant-Subdomain': activeSub
                }
            });
            if (ordersRes.ok) {
                const data = await ordersRes.json();
                if (data.success && Array.isArray(data.orders)) {
                    const relevantOrders = isOwner
                        ? data.orders
                        : data.orders.filter(o => !o.branch_id || String(o.branch_id) === String(activeBranchId));
                    
                    setOrders(prev => {
                        const merged = mergeOrdersSafely(relevantOrders, prev);
                        saveOrdersOfflineCache(getOfflineCacheKey('react_pos_orders'), merged);
                        return merged;
                    });
                }
            }
        } catch (err) {
            console.error("Failed to sync tenant orders from database:", err);
        }

        try {
            // Sync Tables from DB
            const tablesRes = await fetch(`${API_BASE_URL}/tenant/tables${tableBranchQ}`, {
                headers: { 
                    'X-Tenant-Subdomain': activeSub
                }
            });
            if (tablesRes.ok) {
                const data = await tablesRes.json();
                if (data.success && Array.isArray(data.tables)) {
                    setTables(prev => {
                        const mapped = mergeTablesSafely(data.tables, prev);
                        localStorage.setItem(getOfflineCacheKey('react_pos_tables'), JSON.stringify(mapped));
                        return mapped;
                    });
                }
            }
        } catch (err) {
            console.error("Failed to sync tenant tables from database:", err);
        }

        try {
            // Sync Sections from DB
            const sectionsRes = await fetch(`${API_BASE_URL}/tenant/sections${tableBranchQ}`, {
                headers: { 
                    'X-Tenant-Subdomain': activeSub
                }
            });
            if (sectionsRes.ok) {
                const data = await sectionsRes.json();
                if (data.success && Array.isArray(data.sections)) {
                    setSections(data.sections);
                    localStorage.setItem('react_pos_sections', JSON.stringify(data.sections));
                }
            }
        } catch (err) {
            console.error("Failed to sync tenant sections from database:", err);
        }

        try {
            // Sync Branches from DB
            const branchesRes = await fetch(`${API_BASE_URL}/tenant/branches`, {
                headers: { 'X-Tenant-Subdomain': activeSub }
            });
            if (branchesRes.ok) {
                const data = await branchesRes.json();
                if (data.success && Array.isArray(data.branches)) {
                    setBranches(data.branches);
                    localStorage.setItem('react_pos_branches', JSON.stringify(data.branches));
                    try {
                        const u = JSON.parse(sessionStorage.getItem('react_pos_current_user') || localStorage.getItem('react_pos_current_user') || '{}');
                        if (u?.branch_id) {
                            const currentBranch = data.branches.find(b => b.id === parseInt(u.branch_id));
                            if (currentBranch) setBranchSettings(currentBranch);
                        }
                    } catch(e) {}
                }
            }
        } catch (err) {
            console.error("Failed to sync tenant branches from database:", err);
        }

        try {
            // Sync Inventory from DB (Owner gets all for multi-branch view, Operator gets active branch)
            const invUrl = `${API_BASE_URL}/tenant/inventory?tenant=${encodeURIComponent(activeSub)}${branchQ}`;
            const invRes = await fetch(invUrl, {
                headers: { 
                    'X-Tenant-Subdomain': activeSub,
                    'X-Branch-Id': String(activeBranchId || 1)
                }
            });
            if (invRes.ok) {
                const data = await invRes.json();
                if (data.success && Array.isArray(data.inventory)) {
                    setInventory(data.inventory);
                    localStorage.setItem('react_pos_inventory', JSON.stringify(data.inventory));
                }
            }
        } catch (err) {
            console.error("Failed to sync tenant inventory from database:", err);
        }

        try {
            // Sync Recipes from DB
            const recUrl = `${API_BASE_URL}/tenant/recipes?tenant=${encodeURIComponent(activeSub)}${branchQ}`;
            const recRes = await fetch(recUrl, {
                headers: { 
                    'X-Tenant-Subdomain': activeSub,
                    'X-Branch-Id': String(activeBranchId || 1)
                }
            });
            if (recRes.ok) {
                const data = await recRes.json();
                if (data.success && Array.isArray(data.recipes)) {
                    setRecipes(data.recipes);
                    localStorage.setItem('react_pos_recipes', JSON.stringify(data.recipes));
                }
            }
        } catch (err) {
            console.error("Failed to sync tenant recipes from database:", err);
        }
        try {
            // Sync Categories from DB
            const catRes = await fetch(`${API_BASE_URL}/tenant/categories${tableBranchQ}`, {
                headers: { 
                    'X-Tenant-Subdomain': activeSub
                }
            });
            if (catRes.ok) {
                const data = await catRes.json();
                if (data.success && Array.isArray(data.categories)) {
                    const branchCategories = data.categories.filter(c => !c.branch_id || String(c.branch_id) === String(activeBranchId));
                    setCategories(branchCategories);
                    localStorage.setItem(getOfflineCacheKey('react_pos_categories'), JSON.stringify(branchCategories));
                }
            }
        } catch (err) {
            console.error("Failed to sync tenant categories from database:", err);
        }

        try {
            // Sync Menu Items from DB
            const menuRes = await fetch(`${API_BASE_URL}/tenant/menu${tableBranchQ}`, {
                headers: { 
                    'X-Tenant-Subdomain': activeSub
                }
            });
            if (menuRes.ok) {
                const data = await menuRes.json();
                if (data.success && Array.isArray(data.menu_items)) {
                    const branchItems = data.menu_items.filter(item => !item.branch_id || String(item.branch_id) === String(activeBranchId));
                    const mapped = branchItems.map(item => ({
                        id: item.id,
                        branch_id: item.branch_id,
                        name: item.name,
                        nameMr: item.name_mr || item.name,
                        nameHi: item.name_hi || item.name,
                        category: item.category,
                        category_id: item.category_id,
                        price: parseFloat(item.price),
                        display_order: item.display_order !== undefined && item.display_order !== null ? parseInt(item.display_order) : 0,
                        veg: item.veg === 1 || item.veg === true || item.veg === '1' || item.is_veg === 1 || item.is_veg === true,
                        is_veg: item.veg === 1 || item.veg === true || item.veg === '1' || item.is_veg === 1 || item.is_veg === true ? 1 : 0,
                        isAvailable: !!item.is_available,
                        is_available: !!item.is_available,
                        image_url: item.image_url
                    }));
                    mapped.sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
                    setMenuItems(mapped);
                    localStorage.setItem(getOfflineCacheKey('react_pos_menu_items'), JSON.stringify(mapped));
                }
            }
        } catch (err) {
            console.error("Failed to sync tenant menu from database:", err);
        }
    };

    // On-Demand & Automatic Offline Order Sync Action
    const syncOfflineOrders = async (silent = false) => {
        const queueStr = localStorage.getItem('dinexa_offline_orders_queue');
        if (!queueStr) {
            if (!silent) showToast('ℹ️ ऑफलाईन सिंक करण्यासाठी कोणतीही प्रलंबित ऑर्डर उपलब्ध नाही.');
            return { success: true, count: 0 };
        }
        let queue = [];
        try {
            queue = JSON.parse(queueStr);
        } catch (e) {
            queue = [];
        }
        if (!Array.isArray(queue) || queue.length === 0) {
            if (!silent) showToast('ℹ️ ऑफलाईन सिंक करण्यासाठी कोणतीही प्रलंबित ऑर्डर उपलब्ध नाही.');
            return { success: true, count: 0 };
        }

        if (!silent) showToast(`⚡ ${queue.length} ऑफलाईन ऑर्डर्स ऑनलाईन सर्व्हरवर सेव्ह होत आहेत...`);

        try {
            const activeSub = getActiveSubdomain();
            const activeBranchId = parseInt(getActiveBranchId()) || 1;
            const queueWithBranch = queue.map(item => ({
                ...item,
                branch_id: item.branch_id || activeBranchId
            }));

            const response = await fetch(`${API_BASE_URL}/tenant/orders/sync-offline?tenant=${encodeURIComponent(activeSub)}&branch_id=${activeBranchId}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Tenant-Subdomain': activeSub
                },
                body: JSON.stringify({ orders: queueWithBranch, branch_id: activeBranchId })
            });

            const data = await response.json();
            if (response.ok && data.success && data.synced_count > 0) {
                // Immediately free up tables for all settled orders in offline queue
                const settledTableIds = new Set(
                    queue.filter(q => q.status === 'served' && q.table_id && q.table_id !== 'counter')
                         .map(q => String(q.table_id))
                );
                if (settledTableIds.size > 0) {
                    setTables(prev => {
                        const updated = (prev || []).map(t => {
                            const cleanT = String(t.number || t.id).replace(/[^0-9]/g, '');
                            if (settledTableIds.has(String(t.id)) || settledTableIds.has(String(t.number)) || (cleanT && settledTableIds.has(cleanT))) {
                                return { ...t, status: 'vacant', activeOrderId: null };
                            }
                            return t;
                        });
                        safeSetLocalStorage(getOfflineCacheKey('react_pos_tables'), updated);
                        return updated;
                    });
                }
                localStorage.setItem('dinexa_offline_orders_queue', JSON.stringify([]));
                setOfflineOrdersQueue([]);
                localStorage.setItem('dinexa_last_online_sync_time', Date.now().toString());
                showToast(`✅ ${data.synced_count} ऑफलाईन ऑर्डर्स ऑनलाईन डेटाबेसवर यशस्वीरीत्या सेव्ह झाल्या!`);
                // Immediately refresh live state from DB
                await syncTenantDataFromDatabase();
                return { success: true, count: data.synced_count };
            } else if (response.ok && data.success && data.synced_count === 0 && queue.length === 0) {
                localStorage.setItem('dinexa_offline_orders_queue', JSON.stringify([]));
                setOfflineOrdersQueue([]);
                return { success: true, count: 0 };
            } else {
                showToast(`❌ ऑनलाईन सिंक त्रुटी: ${data.error || 'सर्व्हरने ऑर्डर्स सेव्ह केल्या नाहीत. Queue सुरक्षित ठेवली आहे.'}`);
                return { success: false, error: data.error };
            }
        } catch (err) {
            if (!silent) showToast(`❌ नेट जोडणी त्रुटी: ऑफलाईन सिंक अयशस्वी. नेट सुरू असल्याची खात्री करा.`);
            return { success: false, error: err.message };
        }
    };

    // Emergency Recover Stuck Orders (Auto-settle today's cooking/pending orders)
    const emergencyRecoverStuckOrders = async () => {
        try {
            const activeSub = getActiveSubdomain();
            const u = JSON.parse(sessionStorage.getItem('react_pos_current_user') || localStorage.getItem('react_pos_current_user') || '{}');
            const branchId = u?.branch_id || '';

            const res = await fetch(`${API_BASE_URL}/tenant/orders/emergency-recover?tenant=${encodeURIComponent(activeSub)}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Tenant-Subdomain': activeSub
                },
                body: JSON.stringify({ branch_id: branchId })
            });
            const data = await res.json();
            if (res.ok && data.success) {
                await syncTenantDataFromDatabase();
                return data;
            }
            return { success: false, error: data.error || 'Recovery failed' };
        } catch (e) {
            return { success: false, error: e.message };
        }
    };

    // Quick Missing Bill Direct Insert
    const quickInsertMissingBill = async (billData) => {
        try {
            const activeSub = getActiveSubdomain();
            const u = JSON.parse(sessionStorage.getItem('react_pos_current_user') || localStorage.getItem('react_pos_current_user') || '{}');
            const branchId = u?.branch_id || 1;

            const res = await fetch(`${API_BASE_URL}/tenant/orders/quick-insert?tenant=${encodeURIComponent(activeSub)}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Tenant-Subdomain': activeSub
                },
                body: JSON.stringify({
                    ...billData,
                    branch_id: branchId
                })
            });
            const data = await res.json();
            if (res.ok && data.success) {
                await syncTenantDataFromDatabase();
                showToast(`✅ ${data.message || 'बिल सेव्ह झाले!'}`);
                return data;
            } else {
                showToast(`❌ बिल सेव्ह त्रुटी: ${data.error || 'डेटाबेसमध्ये सेव्ह होऊ शकले नाही.'}`);
                return data;
            }
        } catch (e) {
            showToast(`❌ नेटवर्क त्रुटी: ${e.message}`);
            return { success: false, error: e.message };
        }
    };

    // Reassign Bill to Another Branch (Self-service for Hotel Owner)
    const reassignOrderBranch = async (orderId, targetBranchId) => {
        try {
            const activeSub = getActiveSubdomain();
            const res = await fetch(`${API_BASE_URL}/tenant/orders/reassign-branch?tenant=${encodeURIComponent(activeSub)}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Tenant-Subdomain': activeSub
                },
                body: JSON.stringify({
                    order_id: orderId,
                    branch_id: parseInt(targetBranchId)
                })
            });
            const data = await res.json();
            if (res.ok && data.success) {
                await syncTenantDataFromDatabase();
                showToast(`✅ ऑर्डर ${orderId} यशस्वीरीत्या नवीन शाखेत वर्ग (Reassign) केली!`);
                return { success: true };
            } else {
                showToast(`❌ शाखा बदल अयशस्वी: ${data.error || 'Unknown error'}`);
                return { success: false, error: data.error };
            }
        } catch (e) {
            showToast(`❌ एरर: ${e.message}`);
            return { success: false, error: e.message };
        }
    };

    // Scan Orders for Branch Mismatches, Stuck KOTs & Data Issues (Preview Mode)
    const scanOrdersAudit = async () => {
        try {
            const activeSub = getActiveSubdomain();
            const res = await fetch(`${API_BASE_URL}/tenant/orders/audit-and-fix?tenant=${encodeURIComponent(activeSub)}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Tenant-Subdomain': activeSub
                },
                body: JSON.stringify({ action: 'scan' })
            });
            const data = await res.json();
            return data;
        } catch (e) {
            showToast(`❌ तपासणी त्रुटी: ${e.message}`);
            return { success: false, error: e.message };
        }
    };

    // Apply Fixes to Selected / All Detected Orders
    const applyOrdersFix = async (orderIds = []) => {
        try {
            const activeSub = getActiveSubdomain();
            showToast('⏳ ऑर्डर्स दुरुस्त करण्याचे काम सुरू आहे...');
            const res = await fetch(`${API_BASE_URL}/tenant/orders/audit-and-fix?tenant=${encodeURIComponent(activeSub)}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Tenant-Subdomain': activeSub
                },
                body: JSON.stringify({ 
                    action: 'fix',
                    order_ids: orderIds 
                })
            });
            const data = await res.json();
            if (res.ok && data.success) {
                await syncTenantDataFromDatabase();
                showToast(data.message || `✅ ${data.fixed_count || 0} ऑर्डर्स यशस्वीरीत्या दुरुस्त झाल्या!`);
                return data;
            } else {
                showToast(`❌ दुरुस्ती त्रुटी: ${data.error || 'दुरुस्ती अयशस्वी'}`);
                return { success: false, error: data.error };
            }
        } catch (e) {
            showToast(`❌ एरर: ${e.message}`);
            return { success: false, error: e.message };
        }
    };

    // Force Settle a stuck/cooking order to served sales ledger (Owner Action)
    const forceSettleOrder = async (orderId, paymentMethod = 'cash') => {
        try {
            const activeSub = getActiveSubdomain();
            const activeBranchId = parseInt(getActiveBranchId()) || 1;
            const targetOrder = (orders || []).find(o => String(o.id) === String(orderId) || String(o.order_id) === String(orderId));
            const subtotal = targetOrder ? (Number(targetOrder.subtotal) || 0) : 0;
            const tax = targetOrder ? (Number(targetOrder.tax) || 0) : 0;
            const total = targetOrder ? (Number(targetOrder.total) || 0) : 0;

            showToast('⏳ बिल सेटल होत आहे...');
            const res = await fetch(`${API_BASE_URL}/tenant/orders/settle?tenant=${encodeURIComponent(activeSub)}&branch_id=${activeBranchId}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Tenant-Subdomain': activeSub
                },
                body: JSON.stringify({
                    order_id: orderId,
                    payment_method: paymentMethod || 'cash',
                    subtotal: subtotal,
                    tax: tax,
                    total: total,
                    branch_id: targetOrder?.branch_id || activeBranchId
                })
            });
            const data = await res.json();
            if (res.ok && data.success) {
                await syncTenantDataFromDatabase();
                playBillSettledSound();
                showToast(`✅ बिल ${orderId} यशस्वीरीत्या सेटल (Paid) झाले!`);
                return { success: true };
            } else {
                showToast(`❌ बिल सेटल करताना त्रुटी: ${data.error || 'सर्व्हर एरर'}`);
                return { success: false, error: data.error };
            }
        } catch (e) {
            showToast(`❌ एरर: ${e.message}`);
            return { success: false, error: e.message };
        }
    };

    // Auto-detect and fix mismatched bills across branches (Legacy alias)
    const autoFixBranchMismatches = async () => {
        return applyOrdersFix([]);
    };

    // Network status listener (Passive + Auto-sync on Reconnect)
    useEffect(() => {
        const handleOnline = () => {
            setIsOnline(true);
            showToast('🌐 इंटरनेट जोडणी सुरु झाली. ऑटो-सिंक तपासत आहे...');
            // Automatically trigger offline sync on internet recovery
            syncOfflineOrders(true);
        };

        const handleOffline = () => {
            setIsOnline(false);
            showToast('⚡ इंटरनेट जोडणी बंद झाली. (Offline Mode चालू)');
        };

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    // Check Subscription Security Expiry Lock
    const checkSubscriptionSecurity = () => {
        if (!currentUser) return;

        const subExpiresAt = currentUser.subscription_expires_at;
        const now = new Date();

        // 1. Check Hard Expiry Date
        if (subExpiresAt) {
            const expDate = new Date(subExpiresAt);
            if (now > expDate) {
                setIsSubscriptionBlocked(true);
                setSubscriptionLockReason('तुमच्या प्लॅनची मुदत संपली आहे. बिलिंग सुरू ठेवण्यासाठी रिचार्ज करा!');
                return;
            }
        }

        // 2. Check 3 Days Max Offline Limit
        const lastSync = localStorage.getItem('dinexa_last_online_sync_time');
        if (lastSync) {
            const daysOffline = (now.getTime() - parseInt(lastSync, 10)) / (1000 * 3600 * 24);
            if (!navigator.onLine && daysOffline > 3) {
                setIsSubscriptionBlocked(true);
                setSubscriptionLockReason('मागील ३ दिवसांपासून इंटरनेट बंद आहे. सुरक्षेसाठी ५ मिनिटांसाठी इंटरनेट सुरु करून ऑर्डर्स सिंक करा.');
                return;
            }
        }

        // 3. Clock Tamper Check
        const lastOrderTime = localStorage.getItem('dinexa_last_order_timestamp');
        if (lastOrderTime && now.getTime() < parseInt(lastOrderTime, 10) - 300000) {
            setIsSubscriptionBlocked(true);
            setSubscriptionLockReason('मोबाईल/संगणकाची तारीख व वेळ बदललेली आढळली आहे. कृपया योग्य तारीख ठेवा.');
            return;
        }

        setIsSubscriptionBlocked(false);
    };

    useEffect(() => {
        checkSubscriptionSecurity();
    }, [currentUser, isOnline]);

    useEffect(() => {
        if (allowedMenus !== undefined) {
            sessionStorage.setItem('react_pos_allowed_menus', JSON.stringify(allowedMenus));
            localStorage.setItem('react_pos_allowed_menus', JSON.stringify(allowedMenus));
        }
    }, [allowedMenus]);

    useEffect(() => {
        if (lockedActions !== undefined) {
            sessionStorage.setItem('react_pos_locked_actions', JSON.stringify(lockedActions));
            localStorage.setItem('react_pos_locked_actions', JSON.stringify(lockedActions));
        }
    }, [lockedActions]);

    // Sync Orders directly with live MySQL database on tenant login / app mount
    useEffect(() => {
        if (isLoggedIn) {
            syncTenantDataFromDatabase();
        }
    }, [isLoggedIn, tenantSubdomain, currentUser?.branch_id]);

    // Daily Accounting Ledger
    const [accountingLedger, setAccountingLedger] = useState(() => {
        const saved = localStorage.getItem('react_pos_accounting_ledger');
        return saved ? JSON.parse(saved) : [];
    });

    const [branches, setBranches] = useState([]);
    const [branchSettings, setBranchSettings] = useState(null);
    const [bizAnalytics, setBizAnalytics] = useState(null);

    const fetchBizAnalytics = async (period = 'today', branchId = '', fromDate = '', toDate = '') => {
        const activeSub = getActiveSubdomain();
        if (!activeSub || activeSub === 'demo') return;
        try {
            const queryParams = new URLSearchParams();
            queryParams.append('tenant', activeSub);
            queryParams.append('period', period);
            if (branchId && branchId !== 'all') {
                queryParams.append('branch_id', branchId);
            }
            if (period === 'custom') {
                if (fromDate) queryParams.append('from_date', fromDate);
                if (toDate) queryParams.append('to_date', toDate);
            }
            const analyticsRes = await fetch(`${API_BASE_URL}/tenant/biz-analytics?${queryParams.toString()}`, {
                headers: { 'X-Tenant-Subdomain': activeSub }
            });
            const analyticsData = await analyticsRes.json();
            if (analyticsData.success) {
                setBizAnalytics(analyticsData);
            }
        } catch (error) {
            console.error("Failed to fetch biz analytics", error);
        }
    };

    const [cashTallies, setCashTallies] = useState(() => {
        const saved = localStorage.getItem('react_pos_cash_tallies');
        const parsed = safeJsonParse(saved, []);
        return Array.isArray(parsed) ? parsed : [];
    });

    // Pinned Items, Tables, & Sections (📌 Pin feature)
    const [pinnedItems, setPinnedItems] = useState(() => {
        const saved = localStorage.getItem('react_pos_pinned_items');
        const parsed = safeJsonParse(saved, []);
        return Array.isArray(parsed) ? parsed : [];
    });

    const [pinnedTables, setPinnedTables] = useState(() => {
        const saved = localStorage.getItem('react_pos_pinned_tables');
        const parsed = safeJsonParse(saved, []);
        return Array.isArray(parsed) ? parsed : [];
    });

    const [pinnedSections, setPinnedSections] = useState(() => {
        const saved = localStorage.getItem('react_pos_pinned_sections');
        const parsed = safeJsonParse(saved, []);
        return Array.isArray(parsed) ? parsed : [];
    });

    useEffect(() => {
        localStorage.setItem('react_pos_pinned_items', JSON.stringify(pinnedItems));
    }, [pinnedItems]);

    useEffect(() => {
        localStorage.setItem('react_pos_pinned_tables', JSON.stringify(pinnedTables));
    }, [pinnedTables]);


    // Theme Mode: 'light' | 'dark' | 'solid'
    const [themeMode, setThemeMode] = useState(() => {
        return localStorage.getItem('react_pos_theme_mode') || 'light';
    });

    useEffect(() => {
        localStorage.setItem('react_pos_theme_mode', themeMode);
    }, [themeMode]);

    // Checkout Mode Filter for TablesView
    const [checkoutMode, setCheckoutMode] = useState(false);



    // Scoped Cloud Sync Helpers (Callable by specific views like TablesView on demand)
    const fetchCloudTablesOnly = async () => {
        if (!cloudSyncEnabled) return;
        const currentSubdomain = tenantSubdomain || localStorage.getItem('dinexa_active_tenant_subdomain') || localStorage.getItem('dinexa_tenant_subdomain') || 'annapurna';
        if (!currentSubdomain) return;

        try {
            const currentUser = JSON.parse(sessionStorage.getItem('react_pos_current_user') || localStorage.getItem('react_pos_current_user') || '{}');
            const branchQuery = currentUser?.branch_id ? `?branch_id=${currentUser.branch_id}` : '';
            const tablesRes = await fetch(`${API_BASE_URL}/tenant/tables${branchQuery}`, {
                headers: { 'X-Tenant-Subdomain': currentSubdomain }
            });
            if (tablesRes.ok) {
                const tablesData = await tablesRes.json();
                if (tablesData.success && Array.isArray(tablesData.tables) && tablesData.tables.length > 0) {
                    setTables(prev => {
                        const updated = prev.map(pt => {
                            const matched = tablesData.tables.find(bt => 
                                String(bt.id) === String(pt.id) || 
                                String(bt.number) === String(pt.number) || 
                                String(bt.table_number) === String(pt.number) ||
                                String(bt.id) === String(pt.number) ||
                                String(bt.table_number) === String(pt.id)
                            );
                            if (matched) {
                                const newStatus = matched.status || pt.status;
                                if (newStatus === 'billed' && pt.status !== 'billed') {
                                    const isCaptainView = window.location.hash.includes('captain') || window.location.pathname.includes('captain');
                                    if (!isCaptainView) {
                                        playBillRequestSound();
                                        if (typeof showToast === 'function') {
                                            showToast(`🔔 बिल विनंती: Table ${matched.number || pt.number} वरून बिल प्रिंट विनंती काऊंटरकडे आली आहे!`);
                                        }
                                    }
                                }
                                return {
                                    ...pt,
                                    status: newStatus,
                                    billRequested: newStatus === 'billed',
                                    activeOrderId: matched.active_order_id || matched.activeOrderId || pt.activeOrderId
                                };
                            }
                            return pt;
                        });
                        const prevStr = JSON.stringify(prev);
                        const nextStr = JSON.stringify(updated);
                        if (prevStr !== nextStr) {
                            localStorage.setItem('react_pos_tables', nextStr);
                            return updated;
                        }
                        return prev;
                    });
                }
            }

            const ordersRes = await fetch(`${API_BASE_URL}/tenant/orders`, {
                headers: { 'X-Tenant-Subdomain': currentSubdomain }
            });
            if (ordersRes.ok) {
                const ordersData = await ordersRes.json();
                if (ordersData.success && Array.isArray(ordersData.orders) && ordersData.orders.length > 0) {
                    setOrders(prevOrders => {
                        const merged = mergeOrdersSafely(ordersData.orders, prevOrders);
                        return merged;
                    });
                }
            }
        } catch (err) {
            // Silent fail
        }
    };


    useEffect(() => {
        localStorage.setItem('react_pos_pinned_sections', JSON.stringify(pinnedSections));
    }, [pinnedSections]);

    const togglePinItem = (itemId) => {
        setPinnedItems(prev => {
            const isPinned = prev.includes(itemId);
            const next = isPinned ? prev.filter(id => id !== itemId) : [...prev, itemId];
            showToast(isPinned ? 'Pin काढले!' : '📌 आयटम पिन केला!');
            return next;
        });
    };

    const togglePinTable = (tableId) => {
        setPinnedTables(prev => {
            const isPinned = prev.includes(tableId);
            const next = isPinned ? prev.filter(id => id !== tableId) : [...prev, tableId];
            showToast(isPinned ? 'टेबल Pin काढले!' : '📌 टेबल पिन केला!');
            return next;
        });
    };

    const togglePinSection = (secId) => {
        setPinnedSections(prev => {
            const isPinned = prev.includes(secId);
            const next = isPinned ? prev.filter(id => id !== secId) : [...prev, secId];
            showToast(isPinned ? 'सेक्शन Pin काढले!' : '📌 सेक्शन पिन केला!');
            return next;
        });
    };

    const [selectedTableId, setSelectedTableId] = useState(null);
    const [activeCart, setActiveCart] = useState([]);
    const [toast, setToast] = useState({ open: false, message: '' });
    const [sidebarMobileOpen, setSidebarMobileOpen] = useState(false);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

    // Pure Online-First Mode: State is managed in React memory & updated live with MySQL DB.
    // Heavy synchronous localStorage writes and storage listeners are removed to ensure zero UI lag on 2GB RAM tablets.
    useEffect(() => {
        if (currentRole) {
            sessionStorage.setItem('react_pos_current_role', JSON.stringify(currentRole));
            localStorage.setItem('react_pos_current_role', JSON.stringify(currentRole));
        }
    }, [currentRole]);

    // Load active tenant data from backend database dynamically
    useEffect(() => {
        const activeSub = getActiveSubdomain();
        if (!isLoggedIn || !activeSub || activeSub === 'demo') return;

        const fetchTenantData = async () => {
            try {
                let activeBranchId = null;
                try {
                    const u = sessionStorage.getItem('react_pos_current_user') || localStorage.getItem('react_pos_current_user');
                    if (u) activeBranchId = JSON.parse(u)?.branch_id;
                } catch(e) {}
                if (!activeBranchId) {
                    activeBranchId = currentUser?.branch_id || localStorage.getItem('dinexa_active_branch_id') || 1;
                }
                const branchQuery = activeBranchId ? `?branch_id=${activeBranchId}` : '';

                // Fetch Menu Items
                const menuRes = await fetch(`${API_BASE_URL}/tenant/menu${branchQuery}`, {
                    headers: { 'X-Tenant-Subdomain': activeSub }
                });
                const menuData = await menuRes.json();
                if (menuData.success && menuData.menu_items && menuData.menu_items.length > 0) {
                    const branchItems = menuData.menu_items.filter(item => !item.branch_id || String(item.branch_id) === String(activeBranchId));
                    const mapped = branchItems.map(item => ({
                        id: item.id,
                        branch_id: item.branch_id,
                        name: item.name,
                        nameMr: item.name_mr || item.name,
                        nameHi: item.name_hi || item.name,
                        category: item.category,
                        category_id: item.category_id,
                        price: parseFloat(item.price),
                        display_order: item.display_order !== undefined && item.display_order !== null ? parseInt(item.display_order) : (item.displayOrder !== undefined && item.displayOrder !== null ? parseInt(item.displayOrder) : 0),
                        isAvailable: !!item.is_available,
                        image_url: item.image_url
                    }));
                    mapped.sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
                    setMenuItems(mapped);
                    localStorage.setItem(getOfflineCacheKey('react_pos_menu_items'), JSON.stringify(mapped));
                }

                // Fetch Dining Tables
                const tablesRes = await fetch(`${API_BASE_URL}/tenant/tables${branchQuery}`, {
                    headers: { 'X-Tenant-Subdomain': activeSub }
                });
                const tablesData = await tablesRes.json();
                if (tablesData.success && tablesData.tables && tablesData.tables.length > 0) {
                    setTables(prev => {
                        const mapped = mergeTablesSafely(tablesData.tables, prev);
                        localStorage.setItem(getOfflineCacheKey('react_pos_tables'), JSON.stringify(mapped));
                        return mapped;
                    });
                }

                // Fetch Sections
                const sectionsRes = await fetch(`${API_BASE_URL}/tenant/sections${branchQuery}`, {
                    headers: { 'X-Tenant-Subdomain': activeSub }
                });
                const sectionsData = await sectionsRes.json();
                if (sectionsData.success && sectionsData.sections) {
                    setSections(sectionsData.sections);
                }

                // Fetch Orders (Strictly scoped to activeBranchId)
                const ordersRes = await fetch(`${API_BASE_URL}/tenant/orders?tenant=${encodeURIComponent(activeSub)}${branchQuery ? `&branch_id=${activeBranchId}` : ''}`, {
                    headers: { 
                        'X-Tenant-Subdomain': activeSub
                    }
                });
                const ordersData = await ordersRes.json();
                if (ordersData.success && ordersData.orders) {
                    const relevantOrders = ordersData.orders.filter(order => !order.branch_id || String(order.branch_id) === String(activeBranchId));
                    setOrders(prev => {
                        const merged = mergeOrdersSafely(relevantOrders, prev);
                        saveOrdersOfflineCache(getOfflineCacheKey('react_pos_orders'), merged);
                        return merged;
                    });
                }

                // Fetch Categories
                const catRes = await fetch(`${API_BASE_URL}/tenant/categories${branchQuery}`, {
                    headers: { 'X-Tenant-Subdomain': activeSub }
                });
                const catData = await catRes.json();
                if (catData.success && catData.categories) {
                    const branchCategories = catData.categories.filter(c => !c.branch_id || String(c.branch_id) === String(activeBranchId));
                    setCategories(branchCategories);
                    localStorage.setItem(getOfflineCacheKey('react_pos_categories'), JSON.stringify(branchCategories));
                }
            } catch (err) {
                console.error("Failed to load real database data for tenant:", err);
            }
        };

        fetchTenantData();
    }, [isLoggedIn, tenantSubdomain, currentUser?.branch_id]);

    const refreshMenuCatalog = async () => {
        const activeSubdomain = tenantSubdomain || localStorage.getItem('dinexa_active_tenant_subdomain') || 'demo';
        try {
            let activeBranchId = null;
            try {
                const u = sessionStorage.getItem('react_pos_current_user') || localStorage.getItem('react_pos_current_user');
                if (u) activeBranchId = JSON.parse(u)?.branch_id;
            } catch(e) {}
            if (!activeBranchId) {
                activeBranchId = currentUser?.branch_id || localStorage.getItem('dinexa_active_branch_id') || 1;
            }
            const branchQuery = activeBranchId ? `?branch_id=${activeBranchId}` : '';

            const [catRes, menuRes] = await Promise.all([
                fetch(`${API_BASE_URL}/tenant/categories${branchQuery}`, { headers: { 'X-Tenant-Subdomain': activeSubdomain } }),
                fetch(`${API_BASE_URL}/tenant/menu${branchQuery}`, { headers: { 'X-Tenant-Subdomain': activeSubdomain } })
            ]);
            const catData = await catRes.json();
            const menuData = await menuRes.json();

            if (catData.success && catData.categories) {
                const branchCategories = catData.categories.filter(c => !c.branch_id || String(c.branch_id) === String(activeBranchId));
                setCategories(branchCategories);
                localStorage.setItem(getOfflineCacheKey('react_pos_categories'), JSON.stringify(branchCategories));
            }

            if (menuData.success && menuData.menu_items && menuData.menu_items.length > 0) {
                const branchItems = menuData.menu_items.filter(item => !item.branch_id || String(item.branch_id) === String(activeBranchId));
                const mapped = branchItems.map(item => ({
                    id: item.id,
                    branch_id: item.branch_id,
                    name: item.name,
                    nameMr: item.name_mr || item.name,
                    nameHi: item.name_hi || item.name,
                    category: item.category ? (item.category.name || item.category) : (item.category_id || 'General'),
                    category_id: item.category_id,
                    category_name: item.category ? (item.category.name || item.category.name_mr) : '',
                    price: parseFloat(item.price),
                    display_order: item.display_order !== undefined && item.display_order !== null ? parseInt(item.display_order) : (item.displayOrder !== undefined && item.displayOrder !== null ? parseInt(item.displayOrder) : 0),
                    veg: item.veg === 1 || item.veg === true || item.veg === '1' || item.is_veg === 1 || item.is_veg === true,
                    is_veg: item.veg === 1 || item.veg === true || item.veg === '1' || item.is_veg === 1 || item.is_veg === true,
                    isAvailable: !!item.is_available,
                    is_available: !!item.is_available,
                    image_url: item.image_url
                }));
                mapped.sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
                setMenuItems(mapped);
                localStorage.setItem(getOfflineCacheKey('react_pos_menu_items'), JSON.stringify(mapped));
            }
        } catch (err) {
            console.error("Failed to refresh menu catalog:", err);
        }
    };

    // Global Toast Notification Helper
    const showToast = (message) => {
        setToast({ open: true, message });
    };

    const closeToast = () => {
        setToast(prev => ({ ...prev, open: false }));
    };

    // Start a brand new, isolated counter / parcel order
    const startNewParcelOrder = (channel = 'takeaway', aggregatorId = '') => {
        if (selectedTableId && activeCart.length > 0) {
            setTableCarts(prev => ({ ...prev, [selectedTableId]: [...activeCart] }));
        }
        setSelectedTableId('counter');
        setActiveCart([]);
        setTableCarts(prev => ({ ...prev, counter: [] }));
        setActiveView('pos');
        showToast('📦 नवीन पार्सल ऑर्डर: पदार्थ निवडून KOT किंवा थेट बिल सेटल करा.');
    };

    // Table Selection View Trigger (Multi-Table Draft Cart Preservation + On-Demand Fresh Sync)
    const selectTable = (tableId) => {
        if (selectedTableId && activeCart.length > 0) {
            setTableCarts(prev => ({ ...prev, [selectedTableId]: [...activeCart] }));
        }

        setSelectedTableId(tableId);

        // 1. Direct active order ID selection (e.g. from active parcel card click 'ORD-XXXX')
        const directOrder = (orders || []).find(o => String(o.id) === String(tableId) && !['served', 'cancelled'].includes(String(o.status).toLowerCase()));
        if (directOrder) {
            const existingDraft = tableCarts[tableId];
            if (existingDraft && existingDraft.length > 0) {
                setActiveCart([...existingDraft]);
            } else {
                setActiveCart((directOrder.items || []).map(item => ({ ...item })));
            }
            setActiveView('pos');
            if (navigator.onLine && typeof fetchFreshTablesAndOrders === 'function') {
                fetchFreshTablesAndOrders(true).catch(err => console.warn("On-demand table sync error:", err));
            }
            return;
        }

        // 2. Dining Table or Counter lookup
        const table = (tables || []).find(t => String(t.id) === String(tableId) || String(t.number) === String(tableId));
        const existingDraft = tableCarts[tableId];

        if (existingDraft && existingDraft.length > 0) {
            setActiveCart([...existingDraft]);
        } else if (table && tableId !== 'counter') {
            let activeOrder = null;
            if (table.activeOrderId) {
                activeOrder = (orders || []).find(o => String(o.id) === String(table.activeOrderId) && !['served', 'cancelled'].includes(String(o.status).toLowerCase()));
            }
            if (!activeOrder) {
                activeOrder = (orders || []).find(o => {
                    if (['served', 'cancelled'].includes(String(o.status).toLowerCase())) return false;
                    const oTbl = String(o.table_id || o.tableId || '').trim();
                    return oTbl === String(table.id).trim() || (table.number && oTbl === String(table.number).trim());
                });
            }

            if (activeOrder && Array.isArray(activeOrder.items) && activeOrder.items.length > 0) {
                setActiveCart((activeOrder.items || []).map(item => ({ 
                    ...item,
                    id: item.id || item.menu_item_id,
                    menu_item_id: item.menu_item_id || item.id,
                    name: item.name || item.name_mr || item.nameMr || 'Dish',
                    name_mr: item.name_mr || item.nameMr || item.name || 'Dish',
                    price: Number(item.price) || 0,
                    quantity: Number(item.quantity) || 1
                })));
            } else {
                setActiveCart([]);
            }
        } else {
            setActiveCart([]);
        }
        setActiveView('pos');

        // On-Demand Fresh Database Sync without continuous timer polling overhead
        if (navigator.onLine && typeof fetchFreshTablesAndOrders === 'function') {
            fetchFreshTablesAndOrders(true).catch(err => console.warn("On-demand table sync error:", err));
        }
    };

    // Add Item to active cart
    const addToCart = (menuItem) => {
        setActiveCart(prev => {
            const existing = prev.find(item => String(item.id) === String(menuItem.id));
            let updated;
            if (existing) {
                updated = prev.map(item => String(item.id) === String(menuItem.id) ? { ...item, quantity: (Number(item.quantity) || 1) + 1 } : item);
            } else {
                updated = [...prev, { 
                    id: menuItem.id, 
                    menu_item_id: menuItem.id, 
                    name: menuItem.name, 
                    name_mr: menuItem.name_mr || menuItem.nameMr || '', 
                    name_hi: menuItem.name_hi || menuItem.nameHi || '', 
                    price: Number(menuItem.price) || 0, 
                    quantity: 1, 
                    remarks: '' 
                }];
            }
            if (selectedTableId) {
                setTableCarts(tPrev => ({ ...tPrev, [selectedTableId]: updated }));
                if (selectedTableId !== 'counter' && !String(selectedTableId).startsWith('ORD-')) {
                    const currentTable = (tables || []).find(t => String(t.id) === String(selectedTableId) || String(t.number) === String(selectedTableId));
                    if (currentTable && currentTable.status === 'vacant') {
                        setTables(tPrev => (tPrev || []).map(t => (String(t.id) === String(selectedTableId) || String(t.number) === String(selectedTableId)) ? { ...t, status: 'occupied' } : t));
                    }
                }
            }
            return updated;
        });
    };

    // Remove or Decrement Item from active cart
    const removeFromCart = (menuItemId) => {
        setActiveCart(prev => {
            const existing = prev.find(item => String(item.id) === String(menuItemId));
            let updated;
            if (existing && Number(existing.quantity) > 1) {
                updated = prev.map(item => String(item.id) === String(menuItemId) ? { ...item, quantity: Number(item.quantity) - 1 } : item);
            } else {
                updated = prev.filter(item => String(item.id) !== String(menuItemId));
            }
            if (selectedTableId) {
                setTableCarts(tPrev => ({ ...tPrev, [selectedTableId]: updated }));
                if (updated.length === 0 && selectedTableId !== 'counter' && !String(selectedTableId).startsWith('ORD-')) {
                    const table = (tables || []).find(t => String(t.id) === String(selectedTableId) || String(t.number) === String(selectedTableId));
                    if (table && !table.activeOrderId && table.status === 'occupied') {
                        setTables(tPrev => (tPrev || []).map(t => (String(t.id) === String(selectedTableId) || String(t.number) === String(selectedTableId)) ? { ...t, status: 'vacant' } : t));
                    }
                }
            }
            return updated;
        });
    };

    // Update cart item remarks
    const updateRemarks = (menuItemId, remarks) => {
        setActiveCart(prev => {
            const updated = prev.map(item => String(item.id) === String(menuItemId) ? { ...item, remarks } : item);
            if (selectedTableId) {
                setTableCarts(tPrev => ({ ...tPrev, [selectedTableId]: updated }));
            }
            return updated;
        });
    };

    // Clear active cart & table draft cart
    const clearCart = () => {
        setActiveCart([]);
        if (selectedTableId) {
            setTableCarts(tPrev => ({ ...tPrev, [selectedTableId]: [] }));
            if (selectedTableId !== 'counter' && !String(selectedTableId).startsWith('ORD-')) {
                const table = (tables || []).find(t => String(t.id) === String(selectedTableId) || String(t.number) === String(selectedTableId));
                if (table && !table.activeOrderId && table.status === 'occupied') {
                    setTables(tPrev => (tPrev || []).map(t => (String(t.id) === String(selectedTableId) || String(t.number) === String(selectedTableId)) ? { ...t, status: 'vacant' } : t));
                }
            }
        }
    };

    // Send Kitchen Order Ticket (KOT)
    const sendKOT = (passedWaiterName = null, orderSource = 'dine_in', aggregatorOrderId = '') => {
        const waiterName = passedWaiterName || currentUser?.name || currentUser?.username || 'Counter Staff';
        if (!selectedTableId || activeCart.length === 0) return;

        const cleanTblId = String(selectedTableId || '').toLowerCase().trim();
        const isDirectOrder = cleanTblId.startsWith('ord-');
        const isPhysicalTable = Boolean(cleanTblId && cleanTblId !== 'counter' && cleanTblId !== 'null' && cleanTblId !== 'undefined' && !isDirectOrder);
        const table = isPhysicalTable ? (tables || []).find(t => String(t.id).toLowerCase() === cleanTblId || String(t.number).toLowerCase() === cleanTblId) : null;
        let updatedOrders = [...(orders || [])];
        let updatedTables = [...(tables || [])];
        
        // Multi-key active order resolution ONLY for physical tables or direct order edit (ORD-XXXX)
        // Counter takeaway / parcel orders always create a fresh new order unless editing a specific ORD-XXXX!
        let orderId = isDirectOrder 
            ? selectedTableId 
            : (isPhysicalTable 
                ? (table?.activeOrderId || (orders || []).find(o => 
                    !['served', 'cancelled'].includes(String(o.status).toLowerCase()) && 
                    (String(o.table_id).toLowerCase() === cleanTblId || String(o.tableId).toLowerCase() === cleanTblId || (table && (String(o.table_id).toLowerCase() === String(table.number).toLowerCase() || String(o.table_id).toLowerCase() === String(table.id).toLowerCase())))
                  )?.id || null)
                : null);

        // Determine clean order type label and source
        let orderTypeLabel = 'Dine-In';
        let finalOrderSource = 'dine_in';

        if (isPhysicalTable) {
            orderTypeLabel = table ? (table.number ? `Table ${table.number}` : `Table ${table.id}`) : `Table ${selectedTableId}`;
            finalOrderSource = 'dine_in';
        } else {
            if (orderSource === 'zomato') {
                orderTypeLabel = aggregatorOrderId ? `🔴 Zomato (#${aggregatorOrderId})` : '🔴 Zomato Order';
                finalOrderSource = 'zomato';
            } else if (orderSource === 'swiggy') {
                orderTypeLabel = aggregatorOrderId ? `🟠 Swiggy (#${aggregatorOrderId})` : '🟠 Swiggy Order';
                finalOrderSource = 'swiggy';
            } else if (orderSource === 'delivery') {
                orderTypeLabel = '🛵 Direct Delivery';
                finalOrderSource = 'delivery';
            } else {
                orderTypeLabel = '📦 Counter Takeaway';
                finalOrderSource = 'takeaway';
            }
        }

        const activeBranchId = parseInt(getActiveBranchId()) || 1;
        const kotId = generateUniqueKOTId(activeBranchId);
        const currentKOT = {
            id: kotId,
            time: new Date().toISOString(),
            items: activeCart.map(item => ({ 
                id: item.id || item.menu_item_id, 
                menu_item_id: item.menu_item_id || item.id,
                name: item.name || item.name_mr || item.nameMr || 'Dish',
                name_mr: item.name_mr || item.nameMr || item.name || 'Dish',
                quantity: Number(item.quantity) || 1, 
                remarks: item.remarks || '' 
            }))
        };

        if (orderId) {
            // Append KOT / Update existing order with exact active cart items
            let foundExisting = false;
            updatedOrders = updatedOrders.map(order => {
                if (String(order.id) === String(orderId)) {
                    foundExisting = true;
                    // Replace order items with active cart so removed/cancelled items are properly purged
                    const updatedItems = activeCart.map(item => ({ 
                        ...item, 
                        id: item.id || item.menu_item_id,
                        menu_item_id: item.menu_item_id || item.id,
                        name: item.name || item.name_mr || item.nameMr || 'Dish',
                        name_mr: item.name_mr || item.nameMr || item.name || 'Dish',
                        quantity: Number(item.quantity) || 1,
                        price: Number(item.price) || 0
                    }));

                    // Calculate new totals
                    const enableGst = localStorage.getItem('dinexa_gst_enabled') === 'true' || localStorage.getItem('dinexa_gst_enabled') === '1';
                    const subtotal = updatedItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
                    const tax = enableGst ? subtotal * 0.05 : 0;
                    const total = Math.max(0, subtotal + tax - (Number(order.discount) || 0));

                    return {
                        ...order,
                        branch_id: order.branch_id || activeBranchId,
                        table_id: isPhysicalTable ? (table ? table.id : selectedTableId) : 'counter',
                        tableId: isPhysicalTable ? (table ? table.id : selectedTableId) : 'counter',
                        items: updatedItems,
                        kots: [...(order.kots || []), currentKOT],
                        subtotal,
                        tax,
                        total,
                        status: 'preparing',
                        order_source: isPhysicalTable ? 'dine_in' : (finalOrderSource || order.order_source),
                        orderSource: isPhysicalTable ? 'dine_in' : (finalOrderSource || order.orderSource),
                        type: orderTypeLabel
                    };
                }
                return order;
            });

            if (!foundExisting) {
                // If ID was in activeOrderId but order not found in orders array, recreate it
                const enableGst = localStorage.getItem('dinexa_gst_enabled') === 'true' || localStorage.getItem('dinexa_gst_enabled') === '1';
                const subtotal = activeCart.reduce((sum, item) => sum + ((Number(item.price) || 0) * (Number(item.quantity) || 1)), 0);
                const tax = enableGst ? subtotal * 0.05 : 0;
                const newOrder = {
                    id: orderId,
                    branch_id: activeBranchId,
                    tableId: isPhysicalTable ? (table ? table.id : selectedTableId) : 'counter',
                    table_id: isPhysicalTable ? (table ? table.id : selectedTableId) : 'counter',
                    type: orderTypeLabel,
                    order_source: finalOrderSource,
                    orderSource: finalOrderSource,
                    aggregator_order_id: aggregatorOrderId,
                    waiterName,
                    waiter_name: waiterName,
                    timestamp: new Date().toISOString(),
                    status: 'preparing',
                    items: activeCart.map(item => ({ 
                        ...item, 
                        id: item.id || item.menu_item_id, 
                        menu_item_id: item.menu_item_id || item.id, 
                        name: item.name || item.name_mr || item.nameMr || 'Dish',
                        name_mr: item.name_mr || item.nameMr || item.name || 'Dish',
                        quantity: Number(item.quantity) || 1, 
                        price: Number(item.price) || 0 
                    })),
                    kots: [currentKOT],
                    subtotal,
                    tax,
                    discount: 0,
                    total: subtotal + tax
                };
                updatedOrders.push(newOrder);
            }
        } else {
            // Create a brand new active order
            orderId = generateUniqueOrderId(activeBranchId);
            const enableGst = localStorage.getItem('dinexa_gst_enabled') === 'true' || localStorage.getItem('dinexa_gst_enabled') === '1';
            const subtotal = activeCart.reduce((sum, item) => sum + ((Number(item.price) || 0) * (Number(item.quantity) || 1)), 0);
            const tax = enableGst ? subtotal * 0.05 : 0;

            const newOrder = {
                id: orderId,
                branch_id: activeBranchId,
                tableId: isPhysicalTable ? (table ? table.id : selectedTableId) : 'counter',
                table_id: isPhysicalTable ? (table ? table.id : selectedTableId) : 'counter',
                type: orderTypeLabel,
                order_source: finalOrderSource,
                orderSource: finalOrderSource,
                aggregator_order_id: aggregatorOrderId,
                waiterName,
                waiter_name: waiterName,
                timestamp: new Date().toISOString(),
                status: 'preparing',
                items: activeCart.map(item => ({ ...item, id: item.id || item.menu_item_id, menu_item_id: item.menu_item_id || item.id, quantity: Number(item.quantity) || 1, price: Number(item.price) || 0 })),
                kots: [currentKOT],
                subtotal,
                tax,
                discount: 0,
                total: subtotal + tax
            };

            updatedOrders.push(newOrder);
        }

        // Link order to table if it is a physical dining table
        if (isPhysicalTable) {
            updatedTables = updatedTables.map(t => {
                if (String(t.id) === String(selectedTableId) || String(t.number) === String(selectedTableId) || (table && String(t.id) === String(table.id))) {
                    return { ...t, status: 'occupied', activeOrderId: orderId };
                }
                return t;
            });
        }

        setOrders(updatedOrders);
        saveOrdersOfflineCache(getOfflineCacheKey('react_pos_orders'), updatedOrders);
        setTables(updatedTables);
        safeSetLocalStorage(getOfflineCacheKey('react_pos_tables'), updatedTables);
        setActiveCart([]);
        if (selectedTableId) {
            setTableCarts(tPrev => ({ ...tPrev, [selectedTableId]: [] }));
        }
        showToast(`✅ ${kotId} किचनला पाठवली!`);

        // Synchronize order to database
        const finalOrder = updatedOrders.find(o => String(o.id) === String(orderId));
        const activeTenantSubdomain = getActiveSubdomain();
        
        if (finalOrder && activeTenantSubdomain) {
            const payload = {
                id: finalOrder.id,
                order_id: finalOrder.id,
                branch_id: finalOrder.branch_id || activeBranchId,
                table_id: isPhysicalTable ? (table ? (table.id || selectedTableId) : selectedTableId) : 'counter',
                table_number: table ? (table.number || table.id) : (isPhysicalTable ? selectedTableId : null),
                type: orderTypeLabel,
                order_source: isPhysicalTable ? 'dine_in' : finalOrderSource,
                orderSource: isPhysicalTable ? 'dine_in' : finalOrderSource,
                aggregator_order_id: aggregatorOrderId || finalOrder.aggregator_order_id || '',
                status: 'preparing',
                subtotal: finalOrder.subtotal,
                tax: finalOrder.tax,
                total: finalOrder.total,
                waiter_name: waiterName,
                items: finalOrder.items.map(item => ({
                    menu_item_id: item.menu_item_id || item.id,
                    name: item.name || '',
                    quantity: item.quantity,
                    price: item.price,
                    remarks: item.remarks || ''
                }))
            };

            if (!navigator.onLine) {
                saveToOfflineQueue(payload);
                showToast(`✅ KOT ${kotId} ऑफलाईन मेमरीमध्ये सुरक्षित सेव्ह झाली व किचनला पाठवली!`);
                return;
            }

            fetch(`${API_BASE_URL}/tenant/orders?tenant=${encodeURIComponent(activeTenantSubdomain)}&branch_id=${activeBranchId}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Tenant-Subdomain': activeTenantSubdomain
                },
                body: JSON.stringify(payload)
            })
            .then(async res => {
                const data = await res.json();
                if (res.ok && data.success) {
                    showToast(`✅ KOT ${kotId} ऑनलाईन सेव्ह व किचनला पाठवली!`);
                } else {
                    saveToOfflineQueue(payload);
                    showToast(`📦 KOT ${kotId} ऑफलाईन क्यू मध्ये सुरक्षित ठेवली.`);
                }
            })
            .catch(err => {
                saveToOfflineQueue(payload);
                showToast(`📦 KOT ${kotId} ऑफलाईन क्यू मध्ये सुरक्षित ठेवली.`);
            });
        }
    };

    // Settle Bill & Deduct Recipe Inventory (Inward/Outward Integration)
    const settleBill = (discountAmount = 0, serviceChargeAmount = 0, paymentMethod = 'cash', discountReason = '', orderSourceChannel = '', tableIdOverride = null, orderIdOverride = null) => {
        try {
            const activeBranchId = parseInt(getActiveBranchId()) || 1;
            const effectiveTableId = tableIdOverride || selectedTableId || 'counter';
            const table = (tables || []).find(t => String(t.id) === String(effectiveTableId) || String(t.number) === String(effectiveTableId));
            
            let orderId = orderIdOverride || (table ? table.activeOrderId : (String(effectiveTableId).startsWith('ORD-') ? effectiveTableId : null));
            if (!orderId && effectiveTableId !== 'counter') {
                const cleanTNum = String(effectiveTableId).replace(/[^0-9]/g, '');
                const matched = (orders || []).find(o => 
                    (String(o.id) === String(effectiveTableId) || String(o.table_id) === String(effectiveTableId) || String(o.tableId) === String(effectiveTableId) || (table && String(o.table_id) === String(table.number)) || (cleanTNum && String(o.table_id || '').replace(/[^0-9]/g, '') === cleanTNum)) && 
                    !['served', 'cancelled'].includes(String(o.status).toLowerCase())
                );
                if (matched) orderId = matched.id;
            }
            let activeOrder = (orders || []).find(o => String(o.id) === String(orderId));

            const channelType = orderSourceChannel || (activeOrder ? (activeOrder.order_source || activeOrder.orderSource || 'dine_in') : (effectiveTableId === 'counter' ? 'takeaway' : 'dine_in'));
            let orderType = effectiveTableId === 'counter' ? 'Counter Order' : `Table ${table ? table.number : ''}`;
            if (channelType === 'zomato') orderType = '🔴 Zomato Order';
            else if (channelType === 'swiggy') orderType = '🟠 Swiggy Order';
            else if (channelType === 'delivery') orderType = '🛵 Direct Delivery';
            else if (channelType === 'takeaway') orderType = '📦 Counter Takeaway';

            // If no active order existed for this table/cart yet, create order directly from activeCart!
            if (!activeOrder) {
                if (!activeCart || activeCart.length === 0) {
                    showToast("⚠️ कृपया सेव्ह करण्यासाठी कार्टमध्ये पदार्थ जोडा!");
                    return;
                }
                orderId = generateUniqueOrderId(activeBranchId);
                const enableGst = localStorage.getItem('dinexa_gst_enabled') === 'true' || localStorage.getItem('dinexa_gst_enabled') === '1';
                const subtotal = activeCart.reduce((sum, item) => sum + ((Number(item.price) || 0) * (Number(item.quantity) || 1)), 0);
                const tax = enableGst ? subtotal * 0.05 : 0;
                const total = Math.max(0, subtotal + tax + serviceChargeAmount - discountAmount);

                activeOrder = {
                    id: orderId,
                    branch_id: activeBranchId,
                    tableId: table ? table.id : effectiveTableId,
                    table_id: table ? table.id : effectiveTableId,
                    waiterName: currentUser?.name || currentUser?.username || 'Counter / Staff',
                    items: [...activeCart],
                    subtotal,
                    tax,
                    discount: discountAmount,
                    serviceCharge: serviceChargeAmount,
                    total,
                    status: 'served',
                    type: orderType,
                    order_source: channelType,
                    orderSource: channelType,
                    paymentMethod,
                    createdAt: new Date().toISOString()
                };
            } else {
                activeOrder.order_source = channelType;
                activeOrder.orderSource = channelType;
                activeOrder.branch_id = activeOrder.branch_id || activeBranchId;
                if (channelType === 'zomato' || channelType === 'swiggy' || channelType === 'delivery' || channelType === 'takeaway') {
                    activeOrder.type = orderType;
                }
            }

            const enableGst = localStorage.getItem('dinexa_gst_enabled') === 'true' || localStorage.getItem('dinexa_gst_enabled') === '1';
            const finalSubtotal = Number(activeOrder.subtotal) || 0;
            const finalTax = enableGst ? finalSubtotal * 0.05 : 0;
            const finalTotal = Math.max(0, finalSubtotal + finalTax + serviceChargeAmount - discountAmount);

            // 1. Deduct ingredient inventory based on recipes
            let lowStockAlerts = [];
            if (inventory && Array.isArray(inventory) && inventory.length > 0) {
                const updatedInventory = inventory.map(ing => {
                    let quantityUsed = 0;

                    (activeOrder.items || []).forEach(cartItem => {
                        const recipeIngredients = (recipes && typeof recipes === 'object') ? recipes[cartItem.id || cartItem.menu_item_id] : null;
                        if (recipeIngredients && Array.isArray(recipeIngredients)) {
                            const ingUsage = recipeIngredients.find(ri => ri.ingId === ing.id);
                            if (ingUsage) {
                                quantityUsed += ((Number(ingUsage.weight) || 0) * (Number(cartItem.quantity) || 1));
                            }
                        }
                    });

                    if (quantityUsed > 0) {
                        const newQty = Math.max(0, parseFloat((ing.qty - quantityUsed).toFixed(2)));
                        if (newQty <= ing.threshold) {
                            lowStockAlerts.push(ing.name);
                        }
                        return { ...ing, qty: newQty };
                    }
                    return ing;
                });

                // Update local state
                setInventory(updatedInventory);
            }

            // Update order status to served (paid)
            setOrders(prev => {
                const exists = (prev || []).some(o => String(o.id) === String(orderId));
                const updated = exists
                    ? prev.map(o => String(o.id) === String(orderId) ? { ...o, branch_id: o.branch_id || activeBranchId, status: 'served', discount: discountAmount, serviceCharge: serviceChargeAmount, total: finalTotal, paymentMethod } : o)
                    : [{ ...activeOrder, branch_id: activeOrder.branch_id || activeBranchId, status: 'served', discount: discountAmount, serviceCharge: serviceChargeAmount, total: finalTotal, paymentMethod }, ...(prev || [])];
                saveOrdersOfflineCache(getOfflineCacheKey('react_pos_orders'), updated);
                return updated;
            });

            // Set table status to vacant & persist to local cache
            const resolvedTableId = table ? table.id : (effectiveTableId !== 'counter' ? effectiveTableId : null);
            if (resolvedTableId) {
                setTables(prev => {
                    const updated = (prev || []).map(t => (String(t.id) === String(resolvedTableId) || (table && String(t.number) === String(table.number))) ? { ...t, status: 'vacant', activeOrderId: null } : t);
                    safeSetLocalStorage(getOfflineCacheKey('react_pos_tables'), updated);
                    return updated;
                });
            }
            setTableCarts(tPrev => {
                const copy = { ...(tPrev || {}) };
                if (resolvedTableId) delete copy[resolvedTableId];
                delete copy[effectiveTableId];
                return copy;
            });

            setSelectedTableId(null);
            setActiveCart([]);
            playBillSettledSound();

            const tableLabel = table ? `Table ${table.number}` : (effectiveTableId === 'counter' ? 'Counter' : `Table ${effectiveTableId}`);
            showToast(`✅ ${tableLabel} चे बिल (₹${Number(finalTotal).toFixed(0)}) यशस्वीरीत्या सेटल झाले!`);

            if (lowStockAlerts.length > 0) {
                setTimeout(() => {
                    showToast(`⚠️ Low Stock Warning: ${lowStockAlerts.join(', ')} is running low!`);
                }, 1000);
            }

            // Synchronize settlement to database atomically
            const activeTenantSubdomain = getActiveSubdomain();
            const payload = {
                id: orderId,
                order_id: orderId,
                payment_method: paymentMethod,
                discount: discountAmount,
                discount_reason: discountReason,
                subtotal: finalSubtotal,
                tax: finalTax,
                total: finalTotal,
                status: 'served',
                table_id: table ? table.id : effectiveTableId,
                type: activeOrder.type || (table ? `Table ${table.number}` : 'Counter Order'),
                waiter_name: activeOrder.waiterName || currentUser?.name || 'Staff',
                items: (activeOrder.items || []).map(item => ({
                    menu_item_id: item.id || item.menu_item_id,
                    quantity: item.quantity || 1,
                    price: item.price || 0
                })),
                branch_id: activeBranchId
            };

            if (!navigator.onLine) {
                saveToOfflineQueue(payload);
                return;
            }

            fetch(`${API_BASE_URL}/tenant/orders/settle?tenant=${encodeURIComponent(activeTenantSubdomain)}&branch_id=${activeBranchId}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Tenant-Subdomain': activeTenantSubdomain
                },
                body: JSON.stringify(payload)
            })
            .then(async res => {
                const data = await res.json();
                if (res.ok && data.success) {
                    showToast(`✅ बिल ${orderId} ऑनलाईन सेव्ह व सेटल झाले! (₹${finalTotal.toFixed(0)})`);
                } else {
                    saveToOfflineQueue(payload);
                    showToast(`📦 बिल ${orderId} ऑफलाईन सुरक्षित सेव्ह केले.`);
                }
            })
            .catch(err => {
                saveToOfflineQueue(payload);
                showToast(`📦 बिल ${orderId} ऑफलाईन सुरक्षित सेव्ह केले.`);
            });
        } catch (err) {
            console.error("Error inside settleBill:", err);
            showToast("⚠️ बिल सेटल करताना त्रुटी आली: " + (err.message || 'Unknown error'));
        }
    };

    // Cancel Order with multi-key robust clearing
    const cancelOrder = (reason = '', tableIdToClear = null, orderIdToClear = null) => {
        const effectiveTableId = tableIdToClear || selectedTableId;
        const table = (tables || []).find(t => String(t.id) === String(effectiveTableId) || String(t.number) === String(effectiveTableId));
        const orderId = orderIdToClear || (table ? table.activeOrderId : null);

        if (orderId) {
            setOrders(prev => (prev || []).map(o => {
                if (String(o.id) === String(orderId)) {
                    return { ...o, status: 'cancelled', cancel_reason: reason || 'Cancelled by staff' };
                }
                return o;
            }));
        }

        if (effectiveTableId) {
            setTables(prev => (prev || []).map(t => {
                if (String(t.id) === String(effectiveTableId) || String(t.number) === String(effectiveTableId) || (orderId && String(t.activeOrderId) === String(orderId))) {
                    return { ...t, status: 'vacant', activeOrderId: null };
                }
                return t;
            }));
        }

        if (String(selectedTableId) === String(effectiveTableId)) {
            setSelectedTableId(null);
            setActiveCart([]);
        }

        showToast('Order cancelled successfully!');

        const activeSubdomain = tenantSubdomain || localStorage.getItem('dinexa_active_tenant_subdomain') || localStorage.getItem('dinexa_tenant_subdomain') || getTenantFromUrl() || 'annapurna';
        if (orderId && activeSubdomain) {
            fetch(`${API_BASE_URL}/tenant/orders/cancel?tenant=${encodeURIComponent(activeSubdomain)}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Tenant-Subdomain': activeSubdomain
                },
                body: JSON.stringify({
                    order_id: orderId,
                    cancel_reason: reason || 'Cancelled from Table'
                })
            }).catch(err => console.error("Database cancel order sync error:", err));
        }
    };

    // Bulk Clear / Reset Occupied or Ghost Tables
    const bulkClearTables = async (tableIds = null) => {
        try {
            const activeSub = tenantSubdomain || localStorage.getItem('dinexa_active_tenant_subdomain') || localStorage.getItem('dinexa_tenant_subdomain') || getTenantFromUrl() || 'annapurna';
            
            setTables(prev => {
                const updated = (prev || []).map(t => {
                    if (!tableIds || tableIds.includes(t.id)) {
                        return { ...t, status: 'vacant', activeOrderId: null };
                    }
                    return t;
                });
                localStorage.setItem('react_pos_tables', JSON.stringify(updated));
                return updated;
            });

            // Mark any active orders on these tables as cancelled
            setOrders(prev => {
                const updated = (prev || []).map(o => {
                    if ((!tableIds || tableIds.includes(o.tableId) || tableIds.includes(o.table_id)) && (o.status === 'pending' || o.status === 'cooking' || o.status === 'preparing')) {
                        return { ...o, status: 'cancelled', cancel_reason: 'Bulk reset by Owner/Manager' };
                    }
                    return o;
                });
                saveOrdersOfflineCache('react_pos_orders', updated);
                saveOrdersOfflineCache(getOfflineCacheKey('react_pos_orders'), updated);
                return updated;
            });

            window.dispatchEvent(new Event('storage'));
            window.dispatchEvent(new Event('dinexa_local_sync'));

            playSuccessSound();
            showToast('✅ सर्व निवडलेली टेबल्स मोकळी (Vacant) केली!');

            if (activeSub) {
                await fetch(`${API_BASE_URL}/tenant/tables/bulk-clear?tenant=${encodeURIComponent(activeSub)}`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Tenant-Subdomain': activeSub
                    },
                    body: JSON.stringify({
                        branch_id: currentUser?.branch_id || 1,
                        table_ids: tableIds
                    })
                });
            }
        } catch (e) {
            console.error("Bulk clear error:", e);
        }
    };

    // Lightweight Instant Fresh Table & Order Sync from Online Database
    const fetchFreshTablesAndOrders = async (silent = false) => {
        const activeSub = getActiveSubdomain();
        if (!activeSub || activeSub === 'demo') return;
        try {
            const activeBranchId = getActiveBranchId();
            let storedUser = null;
            try {
                const u = sessionStorage.getItem('react_pos_current_user') || localStorage.getItem('react_pos_current_user');
                if (u) storedUser = JSON.parse(u);
            } catch(e) {}
            const isOwner = currentUser?.role === 'owner' || currentUser?.is_owner || storedUser?.role === 'owner' || storedUser?.is_owner;

            const branchOrdersQuery = isOwner ? '' : `&branch_id=${activeBranchId}`;
            const tablesQuery = `?tenant=${encodeURIComponent(activeSub)}&branch_id=${activeBranchId}&_t=${Date.now()}`;
            const ordersQuery = `?tenant=${encodeURIComponent(activeSub)}${branchOrdersQuery}&_t=${Date.now()}`;
            
            const [tablesRes, ordersRes] = await Promise.all([
                fetch(`${API_BASE_URL}/tenant/tables${tablesQuery}`, {
                    headers: { 
                        'X-Tenant-Subdomain': activeSub
                    }
                }),
                fetch(`${API_BASE_URL}/tenant/orders${ordersQuery}`, {
                    headers: { 
                        'X-Tenant-Subdomain': activeSub
                    }
                })
            ]);

            if (tablesRes.ok) {
                const tData = await tablesRes.json();
                if (tData.success && Array.isArray(tData.tables)) {
                    setTables(prev => {
                        const mappedTables = mergeTablesSafely(tData.tables, prev);
                        localStorage.setItem(getOfflineCacheKey('react_pos_tables'), JSON.stringify(mappedTables));
                        return mappedTables;
                    });
                }
            }

            if (ordersRes.ok) {
                const oData = await ordersRes.json();
                if (oData.success && Array.isArray(oData.orders)) {
                    const relevantOrders = isOwner
                        ? oData.orders
                        : oData.orders.filter(o => !o.branch_id || String(o.branch_id) === String(activeBranchId));

                    setOrders(prev => {
                        const merged = mergeOrdersSafely(relevantOrders, prev);
                        saveOrdersOfflineCache(getOfflineCacheKey('react_pos_orders'), merged);
                        return merged;
                    });
                }
            }

            window.dispatchEvent(new Event('storage'));
            window.dispatchEvent(new Event('dinexa_local_sync'));

            if (!silent) {
                showToast('🔄 टेबल्स व ऑर्डर्स यशस्वीरित्या रिफ्रेश झाले!');
            }
        } catch (err) {
            console.error("Refresh error:", err);
            if (!silent) {
                showToast('⚠️ सर्व्हरशी संपर्क होऊ शकला नाही.');
            }
        }
    };

    // Shift table active order to a vacant table with permanent Database sync
    const shiftTable = async (sourceTableId, targetTableId) => {
        const sourceTable = (tables || []).find(t => String(t.id) === String(sourceTableId));
        const targetTable = (tables || []).find(t => String(t.id) === String(targetTableId));
        
        if (!sourceTable || !targetTable) {
            showToast("⚠️ टेबल शिफ्टिंग अयशस्वी: मूळ किंवा नवीन टेबल सापडले नाही.");
            return;
        }

        // Resolve active order ID
        let activeOrderId = sourceTable.activeOrderId;
        if (!activeOrderId) {
            const foundOrder = (orders || []).find(o => 
                (String(o.table_id) === String(sourceTableId) || String(o.tableId) === String(sourceTableId)) && 
                !['served', 'cancelled', 'paid', 'settled'].includes(String(o.status).toLowerCase())
            );
            activeOrderId = foundOrder ? foundOrder.id : null;
        }

        const currentStatus = sourceTable.status === 'occupied' || sourceTable.status === 'billed' ? sourceTable.status : 'occupied';
        const targetLabel = targetTable.number || targetTable.id;
        const sourceLabel = sourceTable.number || sourceTable.id;

        // 1. Update tables state & offline cache
        const updatedTables = (tables || []).map(t => {
            if (String(t.id) === String(sourceTableId)) {
                return { ...t, status: 'vacant', activeOrderId: null };
            }
            if (String(t.id) === String(targetTableId)) {
                return { ...t, status: currentStatus, activeOrderId: activeOrderId };
            }
            return t;
        });
        setTables(updatedTables);
        safeSetLocalStorage(getOfflineCacheKey('react_pos_tables'), updatedTables);

        // 2. Update orders tableId and type in state & offline cache
        if (activeOrderId) {
            const updatedOrders = (orders || []).map(o => {
                if (String(o.id) === String(activeOrderId)) {
                    return { 
                        ...o, 
                        tableId: targetTableId, 
                        table_id: targetTableId, 
                        type: `Table ${targetLabel}` 
                    };
                }
                return o;
            });
            setOrders(updatedOrders);
            saveOrdersOfflineCache(getOfflineCacheKey('react_pos_orders'), updatedOrders);
        }

        // 3. Move draft cart items if any
        setTableCarts(tPrev => {
            const copy = { ...(tPrev || {}) };
            if (copy[sourceTableId]) {
                copy[targetTableId] = copy[sourceTableId];
                delete copy[sourceTableId];
            }
            return copy;
        });

        if (selectedTableId === sourceTableId) {
            setSelectedTableId(targetTableId);
        }

        showToast(`✅ टेबल ${sourceLabel} वरून टेबल ${targetLabel} वर ऑर्डर यशस्वीरीत्या शिफ्ट केली!`);

        // 4. Synchronize to Backend Database
        const activeSub = getActiveSubdomain();
        if (activeSub && activeSub !== 'demo') {
            try {
                const shiftRes = await fetch(`${API_BASE_URL}/tenant/tables/shift?tenant=${encodeURIComponent(activeSub)}`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Tenant-Subdomain': activeSub
                    },
                    body: JSON.stringify({
                        source_table_id: sourceTableId,
                        target_table_id: targetTableId,
                        order_id: activeOrderId
                    })
                });
                const shiftData = await shiftRes.json();
                if (!shiftData.success) {
                    console.warn("Server shift warning:", shiftData.error || shiftData.message);
                }
            } catch (err) {
                console.error("Failed to sync table shift to database:", err);
            }
        }
    };

    // Settle Counter / Token Bill directly
    const settleCounterBill = (paymentMethod = 'cash', discountAmount = 0, serviceChargeAmount = 0, discountReason = '', orderSourceChannel = 'takeaway') => {
        try {
            if (!activeCart || activeCart.length === 0) return null;

            const activeBranchId = parseInt(getActiveBranchId()) || 1;
            const channelType = orderSourceChannel || 'takeaway';
            let orderType = 'Counter Order';
            if (channelType === 'zomato') orderType = '🔴 Zomato Order';
            else if (channelType === 'swiggy') orderType = '🟠 Swiggy Order';
            else if (channelType === 'delivery') orderType = '🛵 Direct Delivery';
            else if (channelType === 'takeaway') orderType = '📦 Counter Takeaway';

            const orderId = generateUniqueOrderId(activeBranchId);
            const enableGst = localStorage.getItem('dinexa_gst_enabled') === 'true' || localStorage.getItem('dinexa_gst_enabled') === '1';
            const subtotal = activeCart.reduce((sum, item) => sum + ((Number(item.price) || 0) * (Number(item.quantity) || 1)), 0);
            const tax = enableGst ? subtotal * 0.05 : 0;
            const finalTotal = Math.max(0, subtotal + tax + serviceChargeAmount - discountAmount);

            // Calculate daily sequential coupon number starting from 1 each day per branch
            const now = new Date();
            const todayDateStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
            const tenantKey = tenantSubdomain || localStorage.getItem('dinexa_active_tenant_subdomain') || 'demo';
            const counterKey = `dinexa_daily_coupon_seq_${tenantKey}_${activeBranchId}_${todayDateStr}`;

            let storedSeq = parseInt(localStorage.getItem(counterKey) || '0', 10);
            const todayOrdersCount = (orders || []).filter(o => {
                const oDate = (o.created_at || o.timestamp || o.createdAt || '').substring(0, 10);
                return oDate === todayDateStr && (!o.branch_id || String(o.branch_id) === String(activeBranchId));
            }).length;

            const dailyCouponNumber = Math.max(storedSeq, todayOrdersCount) + 1;
            localStorage.setItem(counterKey, dailyCouponNumber.toString());

            // 1. Deduct ingredient inventory based on recipes
            let lowStockAlerts = [];
            if (inventory && Array.isArray(inventory) && inventory.length > 0) {
                const updatedInventory = inventory.map(ing => {
                    let quantityUsed = 0;
                    (activeCart || []).forEach(cartItem => {
                        const recipeIngredients = (recipes && typeof recipes === 'object') ? recipes[cartItem.id || cartItem.menu_item_id] : null;
                        if (recipeIngredients && Array.isArray(recipeIngredients)) {
                            const ingUsage = recipeIngredients.find(ri => ri.ingId === ing.id);
                            if (ingUsage) {
                                quantityUsed += ((Number(ingUsage.weight) || 0) * (Number(cartItem.quantity) || 1));
                            }
                        }
                    });

                    if (quantityUsed > 0) {
                        const newQty = Math.max(0, parseFloat((ing.qty - quantityUsed).toFixed(2)));
                        if (newQty <= ing.threshold) {
                            lowStockAlerts.push(ing.name);
                        }
                        return { ...ing, qty: newQty };
                    }
                    return ing;
                });

                // Update states
                setInventory(updatedInventory);
            }

            // 2. Create the completed order record
            const newOrder = {
                id: orderId,
                branch_id: activeBranchId,
                tableId: 'counter',
                type: orderType,
                order_source: channelType,
                orderSource: channelType,
                token_number: dailyCouponNumber,
                coupon_number: dailyCouponNumber,
                tokenNumber: dailyCouponNumber,
                couponNumber: dailyCouponNumber,
                waiterName: currentUser?.name || currentUser?.username || 'Counter Staff',
                timestamp: new Date().toISOString(),
                status: 'served', // Completed
                items: (activeCart || []).map(item => ({ ...item })),
                kots: [{
                    id: generateUniqueKOTId(activeBranchId),
                    time: new Date().toISOString(),
                    items: (activeCart || []).map(item => ({ id: item.id, quantity: item.quantity, remarks: item.remarks }))
                }],
                subtotal,
                tax,
                serviceCharge: serviceChargeAmount,
                discount: discountAmount,
                total: finalTotal,
                paymentMethod
            };

            // Update states
            setOrders(prev => {
                const updated = [newOrder, ...(prev || [])];
                saveOrdersOfflineCache(getOfflineCacheKey('react_pos_orders'), updated);
                return updated;
            });
            setActiveCart([]);
            playSuccessSound();

            showToast(`टोकन जनरेट झाले! बिल: ₹${finalTotal.toFixed(0)}`);
            if (lowStockAlerts.length > 0) {
                setTimeout(() => {
                    showToast(`⚠️ Low Stock Warning: ${lowStockAlerts.join(', ')} is running low!`);
                }, 1000);
            }

            // Synchronize counter order and settlement to database
            const activeTenantSubdomain = getActiveSubdomain();
            const settledPayload = {
                id: orderId,
                order_id: orderId,
                branch_id: activeBranchId,
                table_id: 'counter',
                type: 'Counter Order',
                token_number: dailyCouponNumber,
                coupon_number: dailyCouponNumber,
                status: 'served',
                subtotal,
                tax,
                total: finalTotal,
                discount: discountAmount,
                discount_reason: discountReason,
                payment_method: paymentMethod,
                waiter_name: currentUser?.name || currentUser?.username || 'Counter Staff',
                items: (newOrder.items || []).map(item => ({
                    menu_item_id: item.id || item.menu_item_id,
                    quantity: item.quantity || 1,
                    price: item.price || 0
                }))
            };

        if (!navigator.onLine) {
            saveToOfflineQueue(settledPayload);
            return newOrder;
        }

        // Direct 1-Step Atomic Settlement Call to database
        fetch(`${API_BASE_URL}/tenant/orders/settle?tenant=${encodeURIComponent(activeTenantSubdomain)}&branch_id=${activeBranchId}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Tenant-Subdomain': activeTenantSubdomain
            },
            body: JSON.stringify(settledPayload)
        })
        .then(res => res.json())
        .then(data => {
            if (data && data.success) {
                showToast(`✅ काऊंटर टोकन ${orderId} डेटाबेसमध्ये सेव्ह व सेटल झाले! (₹${finalTotal.toFixed(0)})`);
            } else {
                saveToOfflineQueue(settledPayload);
            }
        })
        .catch(err => {
            saveToOfflineQueue(settledPayload);
        });

            return newOrder;
        } catch (err) {
            console.error("Error in settleCounterBill:", err);
            showToast("⚠️ टोकन सेव्ह करताना त्रुटी आली: " + (err.message || 'Unknown error'));
            return null;
        }
    };

    // Create new Raw Material (Inventory Item)
    const createInventoryItem = async (itemData) => {
        try {
            const activeSub = getActiveSubdomain();
            const response = await fetch(`${API_BASE_URL}/tenant/inventory`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Tenant-Subdomain': activeSub,
                    'X-Branch-Id': String(currentUser?.branch_id || 1)
                },
                body: JSON.stringify({
                    branch_id: currentUser?.branch_id || 1,
                    name: itemData.name,
                    unit: itemData.unit || 'नग',
                    qty: parseFloat(itemData.qty) || 0,
                    threshold: parseFloat(itemData.threshold) || 5
                })
            });
            const data = await response.json();
            if (data.success && data.item) {
                setInventory(prev => {
                    const updated = [...(prev || []), data.item];
                    localStorage.setItem('react_pos_inventory', JSON.stringify(updated));
                    return updated;
                });
                showToast(`✅ कच्चा माल '${data.item.name}' जोडला!`);
                return { success: true, item: data.item };
            } else {
                showToast(data.error || 'कच्चा माल तयार करता आला नाही.');
                return { success: false };
            }
        } catch (err) {
            console.error('Failed to create inventory item:', err);
            const localItem = {
                id: Date.now(),
                branch_id: currentUser?.branch_id || 1,
                name: itemData.name,
                unit: itemData.unit || 'नग',
                qty: parseFloat(itemData.qty) || 0,
                threshold: parseFloat(itemData.threshold) || 5
            };
            setInventory(prev => {
                const updated = [...(prev || []), localItem];
                localStorage.setItem('react_pos_inventory', JSON.stringify(updated));
                return updated;
            });
            showToast(`✅ कच्चा माल '${localItem.name}' स्थानिकरीत्या जोडला!`);
            return { success: true, item: localItem };
        }
    };

    // Update Raw Material
    const updateInventoryItem = async (id, itemData) => {
        try {
            const activeSub = getActiveSubdomain();
            await fetch(`${API_BASE_URL}/tenant/inventory/${id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Tenant-Subdomain': activeSub
                },
                body: JSON.stringify(itemData)
            });
        } catch (err) {
            console.error('Failed to update inventory item on server:', err);
        }
        setInventory(prev => {
            const updated = (prev || []).map(i => (i.id === id || parseInt(i.id) === parseInt(id)) ? { ...i, ...itemData } : i);
            localStorage.setItem('react_pos_inventory', JSON.stringify(updated));
            return updated;
        });
        showToast('✅ कच्चा माल अपडेट झाला!');
        return { success: true };
    };

    // Delete Raw Material
    const deleteInventoryItem = async (id) => {
        try {
            const activeSub = getActiveSubdomain();
            await fetch(`${API_BASE_URL}/tenant/inventory/${id}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Tenant-Subdomain': activeSub
                }
            });
        } catch (err) {
            console.error('Failed to delete inventory item on server:', err);
        }
        setInventory(prev => {
            const updated = (prev || []).filter(i => !(i.id === id || parseInt(i.id) === parseInt(id)));
            localStorage.setItem('react_pos_inventory', JSON.stringify(updated));
            return updated;
        });
        setRecipes(prev => {
            const updated = (prev || []).filter(r => parseInt(r.ingredient_id) !== parseInt(id));
            localStorage.setItem('react_pos_recipes', JSON.stringify(updated));
            return updated;
        });
        showToast('कच्चा माल हटवला.');
        return { success: true };
    };

    // Inventory Purchase (Stock Inward Entry)
    const addInwardPurchase = async (ingId, qty, rate, vendor, date) => {
        const parsedQty = parseFloat(qty);
        const parsedRate = parseFloat(rate) || 0;
        if (isNaN(parsedQty) || parsedQty <= 0) return;

        const ingredient = (inventory || []).find(i => i.id === ingId || parseInt(i.id) === parseInt(ingId));
        if (!ingredient) return;

        const dateStr = date || new Date().toISOString().split('T')[0];

        // 1. Update state & localStorage
        setInventory(prev => {
            const updated = (prev || []).map(i => {
                if (i.id === ingId || parseInt(i.id) === parseInt(ingId)) {
                    return { ...i, qty: parseFloat(((Number(i.qty) || 0) + parsedQty).toFixed(2)) };
                }
                return i;
            });
            localStorage.setItem('react_pos_inventory', JSON.stringify(updated));
            return updated;
        });

        const newRecord = {
            id: `pur-${Date.now()}`,
            date: dateStr,
            ingId,
            ingName: ingredient.name,
            qty: parsedQty,
            rate: parsedRate,
            totalCost: parsedQty * parsedRate,
            vendor: vendor || 'दैनंदिन आवक (Daily Inward)'
        };

        setInwardLedger(prev => {
            const updated = [newRecord, ...(prev || [])];
            localStorage.setItem('react_pos_inward_ledger', JSON.stringify(updated));
            return updated;
        });

        // 2. Persist to server
        try {
            const activeSub = getActiveSubdomain();
            await fetch(`${API_BASE_URL}/tenant/inventory/inward`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Tenant-Subdomain': activeSub,
                    'X-Branch-Id': String(currentUser?.branch_id || 1)
                },
                body: JSON.stringify({
                    branch_id: currentUser?.branch_id || 1,
                    ingredient_id: ingId,
                    qty: parsedQty,
                    rate: parsedRate,
                    vendor: vendor || '',
                    date: dateStr
                })
            });
        } catch (e) {
            console.error('Failed to sync inward purchase to server:', e);
        }

        showToast(`✅ ${parsedQty} ${ingredient.unit} '${ingredient.name}' साठ्यात जमा केले!`);
    };

    // Log Manual Outward / Spoilage / Wastage
    const addManualOutward = async (ingId, qty, reason, date) => {
        const parsedQty = parseFloat(qty);
        if (isNaN(parsedQty) || parsedQty <= 0 || !reason) return;

        const ingredient = (inventory || []).find(i => i.id === ingId || parseInt(i.id) === parseInt(ingId));
        if (!ingredient) return;

        const dateStr = date || new Date().toISOString().split('T')[0];

        // 1. Update state & localStorage
        setInventory(prev => {
            const updated = (prev || []).map(i => {
                if (i.id === ingId || parseInt(i.id) === parseInt(ingId)) {
                    return { ...i, qty: Math.max(0, parseFloat(((Number(i.qty) || 0) - parsedQty).toFixed(2))) };
                }
                return i;
            });
            localStorage.setItem('react_pos_inventory', JSON.stringify(updated));
            return updated;
        });

        const newRecord = {
            id: `wast-${Date.now()}`,
            date: dateStr,
            ingId,
            ingName: ingredient.name,
            qty: parsedQty,
            reason
        };

        setOutwardLedger(prev => {
            const updated = [newRecord, ...(prev || [])];
            localStorage.setItem('react_pos_outward_ledger', JSON.stringify(updated));
            return updated;
        });

        // 2. Persist to server
        try {
            const activeSub = getActiveSubdomain();
            await fetch(`${API_BASE_URL}/tenant/inventory/outward`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Tenant-Subdomain': activeSub,
                    'X-Branch-Id': String(currentUser?.branch_id || 1)
                },
                body: JSON.stringify({
                    branch_id: currentUser?.branch_id || 1,
                    ingredient_id: ingId,
                    qty: parsedQty,
                    reason,
                    date: dateStr
                })
            });
        } catch (e) {
            console.error('Failed to sync outward wastage to server:', e);
        }

        showToast(`⚠️ ${parsedQty} ${ingredient.unit} '${ingredient.name}' साठ्यातून वजा केले (${reason})!`);
    };

    // Third-party Online Order Aggregation (Zomato/Swiggy)
    const acceptOnlineOrder = (onlineOrderId) => {
        const order = onlineOrders.find(o => o.id === onlineOrderId);
        if (!order) return;

        // Mark as accepted
        setOnlineOrders(prev => prev.filter(o => o.id !== onlineOrderId));

        // Route directly to KDS queue
        const newKdsOrder = {
            id: order.id,
            tableId: null,
            type: `Delivery (${order.source})`,
            waiterName: `${order.source} API`,
            timestamp: new Date().toISOString(),
            status: 'preparing',
            items: order.items.map(item => {
                const menuItem = menuItems.find(m => m.id === item.id);
                return {
                    id: item.id,
                    name: item.name,
                    price: item.price,
                    quantity: item.quantity,
                    remarks: 'Online Order Integration'
                };
            }),
            kots: [{
                id: `KOT-${Math.floor(100 + Math.random() * 900)}`,
                time: new Date().toISOString(),
                items: order.items.map(i => ({ id: i.id, quantity: i.quantity, remarks: 'Online Order' }))
            }],
            subtotal: order.total / 1.05,
            tax: order.total - (order.total / 1.05),
            discount: 0,
            total: order.total
        };

        setOrders(prev => [...prev, newKdsOrder]);
        showToast(`Online order accepted! Routed to KDS.`);
    };

    const rejectOnlineOrder = (onlineOrderId) => {
        setOnlineOrders(prev => prev.filter(o => o.id !== onlineOrderId));
        showToast(`Online order rejected.`);
    };

    // Kitchen Station status progression (Preparing -> Ready -> Served)
    const advanceOrderStatus = (orderId) => {
        setOrders(prev => prev.map(o => {
            if (o.id === orderId) {
                if (o.status === 'preparing') {
                    // Update table status to ready to bill if linked
                    if (o.tableId) {
                        setTables(tPrev => tPrev.map(t => t.id === o.tableId ? { ...t, status: 'billed' } : t));
                    }
                    showToast(`Order ${orderId} marked as Ready!`);
                    return { ...o, status: 'ready' };
                } else if (o.status === 'ready') {
                    showToast(`Order ${orderId} Served!`);
                    return { ...o, status: 'served' };
                }
            }
            return o;
        }));
    };

    // Calculate Stock Reconciliation stats dynamically
    const getReconciliationData = (targetBranchId = null) => {
        if (!Array.isArray(inventory) || inventory.length === 0) return [];

        const effectiveBranchId = targetBranchId || getActiveBranchId();

        // 🛡️ Filter raw materials strictly by branch so items never duplicate across branches
        const branchInventory = (effectiveBranchId && effectiveBranchId !== 'all')
            ? inventory.filter(i => String(i.branch_id) === String(effectiveBranchId))
            : inventory;

        if (branchInventory.length === 0) return [];

        // 🚀 High-Performance Pre-Indexing scoped strictly by branch
        const inwardMap = {};
        (inwardLedger || []).forEach(rec => {
            if (!effectiveBranchId || effectiveBranchId === 'all' || !rec.branch_id || String(rec.branch_id) === String(effectiveBranchId)) {
                const id = rec.ingredient_id || rec.ingId || rec.item_id;
                if (id !== undefined && id !== null) {
                    inwardMap[id] = (inwardMap[id] || 0) + (Number(rec.quantity || rec.qty || rec.amount) || 0);
                }
            }
        });

        const outwardMap = {};
        (outwardLedger || []).forEach(rec => {
            if (!effectiveBranchId || effectiveBranchId === 'all' || !rec.branch_id || String(rec.branch_id) === String(effectiveBranchId)) {
                const id = rec.ingredient_id || rec.ingId || rec.item_id;
                if (id !== undefined && id !== null) {
                    outwardMap[id] = (outwardMap[id] || 0) + (Number(rec.quantity || rec.qty || rec.amount) || 0);
                }
            }
        });

        // Tally sold dish quantities from served orders: STRICTLY scoped to branch
        const soldDishQuantities = {};
        (orders || []).forEach(o => {
            if (o.status === 'served') {
                if (!effectiveBranchId || effectiveBranchId === 'all' || !o.branch_id || String(o.branch_id) === String(effectiveBranchId)) {
                    (o.items || []).forEach(item => {
                        const dishId = item.menu_item_id || item.id;
                        const q = Number(item.quantity || item.qty) || 1;
                        if (dishId) {
                            soldDishQuantities[dishId] = (soldDishQuantities[dishId] || 0) + q;
                        }
                    });
                }
            }
        });

        // Map recipe ingredients to total sold units
        const salesOutwardMap = {};
        if (Array.isArray(recipes)) {
            recipes.forEach(r => {
                const dishId = r.menu_item_id || r.menuItemId;
                const ingId = r.ingredient_id || r.ingId;
                const dishQty = soldDishQuantities[dishId] || 0;
                if (dishQty > 0 && ingId) {
                    const w = Number(r.weight || r.quantity || r.qty || 0);
                    salesOutwardMap[ingId] = (salesOutwardMap[ingId] || 0) + (dishQty * w);
                }
            });
        } else if (recipes && typeof recipes === 'object') {
            Object.keys(recipes).forEach(dishId => {
                const list = recipes[dishId];
                const dishQty = soldDishQuantities[dishId] || 0;
                if (dishQty > 0 && Array.isArray(list)) {
                    list.forEach(ri => {
                        const ingId = ri.ingredient_id || ri.ingId;
                        if (ingId) {
                            const w = Number(ri.weight || ri.quantity || ri.qty || 0);
                            salesOutwardMap[ingId] = (salesOutwardMap[ingId] || 0) + (dishQty * w);
                        }
                    });
                }
            });
        }

        // Instant O(I) linear mapping for branch items only
        return branchInventory.map(ing => {
            const ingId = ing.id;
            const inward = inwardMap[ingId] || 0;
            const salesOutward = salesOutwardMap[ingId] || 0;
            const manualOutward = outwardMap[ingId] || 0;
            const closing = Number(ing.qty ?? ing.quantity) || 0;
            const opening = closing - inward + salesOutward + manualOutward;

            return {
                id: ing.id,
                branch_id: ing.branch_id,
                name: ing.name,
                nameMr: ing.name_mr || ing.nameMr || ing.name,
                nameHi: ing.name_hi || ing.nameHi || ing.name,
                unit: ing.unit || 'kg',
                opening: parseFloat((Number(opening) || 0).toFixed(2)),
                inward: parseFloat((Number(inward) || 0).toFixed(2)),
                salesOutward: parseFloat((Number(salesOutward) || 0).toFixed(2)),
                manualOutward: parseFloat((Number(manualOutward) || 0).toFixed(2)),
                closing: parseFloat((Number(closing) || 0).toFixed(2)),
                threshold: Number(ing.threshold) || 0
            };
        });
    };

    // Owner Settings & Recipe Actions
    const updateMenuPrice = async (itemId, newPrice) => {
        const val = parseFloat(newPrice);
        if (isNaN(val) || val < 0) return;
        
        const item = menuItems.find(m => m.id === itemId);
        if (!item) return;

        try {
            const response = await fetch(`${API_BASE_URL}/tenant/menu/${itemId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Tenant-Subdomain': tenantSubdomain
                },
                body: JSON.stringify({
                    name: item.name,
                    name_mr: item.nameMr,
                    name_hi: item.nameHi,
                    category_id: item.category_id,
                    price: val,
                    is_available: item.isAvailable,
                    image_url: item.image_url
                })
            });
            const data = await response.json();
            if (data.success) {
                setMenuItems(prev => prev.map(m => m.id === itemId ? { ...m, price: val } : m));
                showToast(`Price updated for ${item.name}`);
            } else {
                showToast(data.error || 'Failed to update price');
            }
        } catch (err) {
            showToast('Network error');
        }
    };

    const addMenuItem = async (menuItemData) => {
        try {
            const activeSub = tenantSubdomain || localStorage.getItem('dinexa_active_tenant_subdomain') || 'annapurna';
            let storedUserBranch = null;
            try {
                const u = sessionStorage.getItem('react_pos_current_user') || localStorage.getItem('react_pos_current_user');
                if (u) storedUserBranch = JSON.parse(u)?.branch_id;
            } catch(e) {}
            const activeBranchId = menuItemData.branch_id || currentUser?.branch_id || storedUserBranch || localStorage.getItem('dinexa_active_branch_id') || 1;

            const isVegVal = menuItemData.is_veg !== undefined 
                ? (menuItemData.is_veg === 1 || menuItemData.is_veg === true || menuItemData.is_veg === '1' ? 1 : 0) 
                : (menuItemData.veg !== undefined ? (menuItemData.veg ? 1 : 0) : 1);

            const isAvailVal = menuItemData.is_available !== undefined 
                ? (menuItemData.is_available === 1 || menuItemData.is_available === true || menuItemData.is_available === '1' ? 1 : 0) 
                : (menuItemData.isAvailable !== undefined ? (menuItemData.isAvailable ? 1 : 0) : 1);

            const response = await fetch(`${API_BASE_URL}/tenant/menu`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Tenant-Subdomain': activeSub
                },
                body: JSON.stringify({
                    name: menuItemData.name,
                    name_mr: menuItemData.name_mr,
                    name_hi: menuItemData.name_hi,
                    category_id: menuItemData.category_id,
                    price: parseFloat(menuItemData.price),
                    display_order: parseInt(menuItemData.display_order) || 0,
                    is_veg: isVegVal,
                    veg: isVegVal === 1,
                    is_available: isAvailVal,
                    image_url: menuItemData.image_url,
                    branch_id: parseInt(activeBranchId) || 1
                })
            });
            const data = await response.json();
            if (data.success) {
                const branchQuery = `?branch_id=${activeBranchId}`;
                const menuRes = await fetch(`${API_BASE_URL}/tenant/menu${branchQuery}`, {
                    headers: { 'X-Tenant-Subdomain': activeSub }
                });
                const menuData = await menuRes.json();
                if (menuData.success && menuData.menu_items) {
                    const mapped = menuData.menu_items.map(item => ({
                        id: item.id,
                        branch_id: item.branch_id,
                        name: item.name,
                        nameMr: item.name_mr || item.name,
                        nameHi: item.name_hi || item.name,
                        category: item.category,
                        category_id: item.category_id,
                        price: parseFloat(item.price),
                        display_order: item.display_order !== undefined && item.display_order !== null ? parseInt(item.display_order) : (item.displayOrder !== undefined && item.displayOrder !== null ? parseInt(item.displayOrder) : 0),
                        veg: item.veg === 1 || item.veg === true || item.veg === '1' || item.is_veg === 1 || item.is_veg === true,
                        is_veg: item.veg === 1 || item.veg === true || item.veg === '1' || item.is_veg === 1 || item.is_veg === true ? 1 : 0,
                        isAvailable: !!item.is_available,
                        is_available: !!item.is_available,
                        image_url: item.image_url
                    }));
                    mapped.sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
                    setMenuItems(mapped);
                }
                showToast(`Menu item "${menuItemData.name}" created!`);
                return true;
            } else {
                showToast(data.error || 'Failed to create menu item');
                return false;
            }
        } catch (err) {
            showToast('Network error');
            return false;
        }
    };

    const updateMenuItemDetails = async (itemId, menuItemData) => {
        try {
            const activeSub = tenantSubdomain || localStorage.getItem('dinexa_active_tenant_subdomain') || 'annapurna';
            let storedUserBranch = null;
            try {
                const u = sessionStorage.getItem('react_pos_current_user') || localStorage.getItem('react_pos_current_user');
                if (u) storedUserBranch = JSON.parse(u)?.branch_id;
            } catch(e) {}
            const activeBranchId = menuItemData.branch_id || currentUser?.branch_id || storedUserBranch || localStorage.getItem('dinexa_active_branch_id') || 1;

            const isVegVal = menuItemData.is_veg !== undefined 
                ? (menuItemData.is_veg === 1 || menuItemData.is_veg === true || menuItemData.is_veg === '1' ? 1 : 0) 
                : (menuItemData.veg !== undefined ? (menuItemData.veg ? 1 : 0) : 1);

            const isAvailVal = menuItemData.is_available !== undefined 
                ? (menuItemData.is_available === 1 || menuItemData.is_available === true || menuItemData.is_available === '1' ? 1 : 0) 
                : (menuItemData.isAvailable !== undefined ? (menuItemData.isAvailable ? 1 : 0) : 1);

            const response = await fetch(`${API_BASE_URL}/tenant/menu/${itemId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Tenant-Subdomain': activeSub
                },
                body: JSON.stringify({
                    name: menuItemData.name,
                    name_mr: menuItemData.name_mr,
                    name_hi: menuItemData.name_hi,
                    category_id: menuItemData.category_id,
                    price: parseFloat(menuItemData.price),
                    display_order: parseInt(menuItemData.display_order) || 0,
                    is_veg: isVegVal,
                    veg: isVegVal === 1,
                    is_available: isAvailVal,
                    image_url: menuItemData.image_url,
                    branch_id: parseInt(activeBranchId) || 1
                })
            });
            const data = await response.json();
            if (data.success) {
                const branchQuery = `?branch_id=${activeBranchId}`;
                const menuRes = await fetch(`${API_BASE_URL}/tenant/menu${branchQuery}`, {
                    headers: { 'X-Tenant-Subdomain': activeSub }
                });
                const menuData = await menuRes.json();
                if (menuData.success && menuData.menu_items) {
                    const mapped = menuData.menu_items.map(item => ({
                        id: item.id,
                        branch_id: item.branch_id,
                        name: item.name,
                        nameMr: item.name_mr || item.name,
                        nameHi: item.name_hi || item.name,
                        category: item.category,
                        category_id: item.category_id,
                        price: parseFloat(item.price),
                        display_order: item.display_order !== undefined && item.display_order !== null ? parseInt(item.display_order) : (item.displayOrder !== undefined && item.displayOrder !== null ? parseInt(item.displayOrder) : 0),
                        veg: item.veg === 1 || item.veg === true || item.veg === '1' || item.is_veg === 1 || item.is_veg === true,
                        is_veg: item.veg === 1 || item.veg === true || item.veg === '1' || item.is_veg === 1 || item.is_veg === true ? 1 : 0,
                        isAvailable: !!item.is_available,
                        is_available: !!item.is_available,
                        image_url: item.image_url
                    }));
                    mapped.sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
                    setMenuItems(mapped);
                    localStorage.setItem(getOfflineCacheKey('react_pos_menu_items'), JSON.stringify(mapped));
                }
                showToast(`Menu item updated successfully!`);
                return true;
            } else {
                showToast(data.error || 'Failed to update menu item');
                return false;
            }
        } catch (err) {
            showToast('Network error');
            return false;
        }
    };

    const deleteMenuItem = async (itemId) => {
        try {
            const activeSub = tenantSubdomain || localStorage.getItem('dinexa_active_tenant_subdomain') || 'annapurna';
            const response = await fetch(`${API_BASE_URL}/tenant/menu/${itemId}`, {
                method: 'DELETE',
                headers: {
                    'X-Tenant-Subdomain': activeSub
                }
            });
            const data = await response.json();
            if (data.success) {
                setMenuItems(prev => prev.filter(m => m.id !== itemId));
                showToast('Menu item deleted successfully!');
                return true;
            } else {
                showToast(data.error || 'Failed to delete menu item');
                return false;
            }
        } catch (err) {
            showToast('Network error');
            return false;
        }
    };

    const updateRecipeWeight = async (dishId, ingId, newWeight) => {
        const val = parseFloat(newWeight);
        if (isNaN(val) || val < 0) return false;
        
        try {
            const activeSub = getActiveSubdomain();
            const response = await fetch(`${API_BASE_URL}/tenant/recipes`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Tenant-Subdomain': activeSub
                },
                body: JSON.stringify({
                    menu_item_id: parseInt(dishId),
                    ingredient_id: parseInt(ingId),
                    weight: val
                })
            });
            const data = await response.json();
            if (data.success) {
                if (val <= 0) {
                    setRecipes(prev => {
                        const updated = Array.isArray(prev) 
                            ? prev.filter(r => !(parseInt(r.menu_item_id) === parseInt(dishId) && parseInt(r.ingredient_id) === parseInt(ingId)))
                            : [];
                        localStorage.setItem('react_pos_recipes', JSON.stringify(updated));
                        return updated;
                    });
                    showToast('Recipe ingredient removed.');
                } else {
                    setRecipes(prev => {
                        let updated = [];
                        if (Array.isArray(prev)) {
                            const existsIndex = prev.findIndex(r => parseInt(r.menu_item_id) === parseInt(dishId) && parseInt(r.ingredient_id) === parseInt(ingId));
                            if (existsIndex >= 0) {
                                updated = prev.map((r, i) => i === existsIndex ? { ...r, weight: val } : r);
                            } else {
                                updated = [...prev, { id: data.id, menu_item_id: parseInt(dishId), ingredient_id: parseInt(ingId), weight: val }];
                            }
                        } else {
                            updated = [{ id: data.id, menu_item_id: parseInt(dishId), ingredient_id: parseInt(ingId), weight: val }];
                        }
                        localStorage.setItem('react_pos_recipes', JSON.stringify(updated));
                        return updated;
                    });
                    showToast('Recipe updated successfully!');
                }
                return true;
            } else {
                showToast(data.error || 'Failed to update recipe');
                return false;
            }
        } catch (err) {
            console.error(err);
            showToast('Network error while updating recipe');
            return false;
        }
    };

    const deleteRecipeIngredient = async (dishId, ingId, recipeId = null) => {
        try {
            const activeSub = getActiveSubdomain();
            const url = (recipeId && recipeId !== 'null')
                ? `${API_BASE_URL}/tenant/recipes/${recipeId}` 
                : `${API_BASE_URL}/tenant/recipes`;
            const response = await fetch(url, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Tenant-Subdomain': activeSub
                },
                body: JSON.stringify({
                    menu_item_id: parseInt(dishId),
                    ingredient_id: parseInt(ingId)
                })
            });
            const data = await response.json();
            if (data.success) {
                setRecipes(prev => {
                    const updated = Array.isArray(prev) 
                        ? prev.filter(r => !(parseInt(r.menu_item_id) === parseInt(dishId) && parseInt(r.ingredient_id) === parseInt(ingId)) && (!recipeId || r.id !== recipeId))
                        : [];
                    localStorage.setItem('react_pos_recipes', JSON.stringify(updated));
                    return updated;
                });
                showToast('Recipe ingredient removed successfully.');
                return true;
            } else {
                showToast(data.error || 'Failed to remove ingredient');
                return false;
            }
        } catch (err) {
            console.error(err);
            showToast('Network error while removing recipe ingredient');
            return false;
        }
    };

    // Daily Accounting Actions
    const addExpense = (desc, amount, category, paymentSource = 'counter_cash') => {
        const parsedAmount = parseFloat(amount);
        if (isNaN(parsedAmount) || parsedAmount <= 0 || !desc) return;
        const newRecord = {
            id: `acc-${Math.floor(100 + Math.random() * 900)}`,
            date: new Date().toISOString(),
            createdAt: new Date().toISOString(),
            type: 'expense',
            desc,
            amount: parsedAmount,
            category,
            paymentSource: paymentSource || 'counter_cash'
        };
        setAccountingLedger(prev => [newRecord, ...prev]);
        showToast(`Expense of ₹${parsedAmount} logged.`);
    };

    const deleteExpense = (id) => {
        setAccountingLedger(prev => prev.filter(e => e.id !== id));
        showToast('Expense deleted successfully.');
    };

    const updateExpense = (id, updatedFields) => {
        setAccountingLedger(prev => prev.map(e => {
            if (e.id === id) {
                return { ...e, ...updatedFields };
            }
            return e;
        }));
        showToast('Expense updated successfully.');
    };

    const saveCashTally = (openingCash, actualClosingCash, systemSalesCash, totalExpenses) => {
        const parsedOpening = parseFloat(openingCash) || 0;
        const parsedClosing = parseFloat(actualClosingCash) || 0;
        const expectedClosing = parsedOpening + systemSalesCash - totalExpenses;
        const variance = parsedClosing - expectedClosing;

        const newRecord = {
            id: `tally-${Math.floor(100 + Math.random() * 900)}`,
            date: new Date().toISOString(),
            openingCash: parsedOpening,
            systemSalesCash,
            totalExpenses,
            expectedClosing,
            actualClosing: parsedClosing,
            variance
        };

        setCashTallies(prev => [newRecord, ...prev]);
        showToast(`Cash Tally Saved! Variance: ₹${variance.toFixed(0)}`);
    };

    const splitTable = (tableId, count) => {
        setTables(prev => {
            const target = prev.find(t => t.id === tableId);
            if (!target || target.status !== 'vacant') return prev;

            const subTables = [];
            for (let i = 0; i < count; i++) {
                const letter = String.fromCharCode(65 + i);
                subTables.push({
                    id: `${tableId}-${letter.toLowerCase()}`,
                    number: `${target.number}-${letter}`,
                    parentId: tableId,
                    status: 'vacant',
                    activeOrderId: null,
                    isSubTable: true
                });
            }

            const updated = prev.map(t => {
                if (t.id === tableId) {
                    return { ...t, status: 'split' };
                }
                return t;
            });

            const idx = updated.findIndex(t => t.id === tableId);
            updated.splice(idx + 1, 0, ...subTables);
            return updated;
        });
        showToast(`Table split into ${count} sub-tables.`);
    };

    const mergeSubTables = (parentId) => {
        setTables(prev => {
            const subs = prev.filter(t => t.parentId === parentId);
            const anyOccupied = subs.some(t => t.status !== 'vacant');
            if (anyOccupied) {
                return prev;
            }

            const filtered = prev.filter(t => t.parentId !== parentId);
            const updated = filtered.map(t => {
                if (t.id === parentId) {
                    return { ...t, status: 'vacant' };
                }
                return t;
            });
            return updated;
        });
        showToast('Sub-tables merged back successfully.');
    };

    return (
        <AppContext.Provider value={{
            branches,
            branchSettings,
            activeView,
            setActiveView,
            tables,
            setTables,
            orders,
            setOrders,
            onlineOrders,
            inventory,
            inwardLedger,
            outwardLedger,
            selectedTableId,
            setSelectedTableId,
            activeCart,
            setActiveCart,
            tableCarts,
            setTableCarts,
            sections,
            setSections,
            addToCart,
            removeFromCart,
            clearCart,
            updateRemarks,
            selectTable,
            sendKOT,
            settleBill,
            cancelOrder,
            shiftTable,
            settleCounterBill,
            startNewParcelOrder,
            createInventoryItem,
            updateInventoryItem,
            deleteInventoryItem,
            addInwardPurchase,
            addManualOutward,
            acceptOnlineOrder,
            rejectOnlineOrder,
            advanceOrderStatus,
            getReconciliationData,
            categories,
            setCategories,
            menuItems,
            setMenuItems,
            refreshMenuCatalog,
            recipes,
            subscribedModules,
            setSubscribedModules,
            currentRole,
            setCurrentRole,
            accountingLedger,
            cashTallies,
            bizAnalytics,
            fetchBizAnalytics,
            updateMenuPrice,
            updateRecipeWeight,
            deleteRecipeIngredient,
            addMenuItem,
            updateMenuItemDetails,
            deleteMenuItem,
            addExpense,
            deleteExpense,
            updateExpense,
            saveCashTally,
            splitTable,
            mergeSubTables,
            toast,
            showToast,
            closeToast,
            sidebarMobileOpen,
            setSidebarMobileOpen,
            sidebarCollapsed,
            setSidebarCollapsed,
            tenantSubdomain,
            setTenantSubdomain,
            isLoggedIn,
            setIsLoggedIn,
            logout,
            currentUser,
            setCurrentUser,
            allowedMenus,
            setAllowedMenus,
            lockedActions,
            setLockedActions,
            cloudSyncEnabled,
            setCloudSyncEnabled,
            toggleCloudSync,
            receiptTemplate,
            setReceiptTemplate,
            printFeedLines,
            setPrintFeedLines,
            pinnedItems,
            setPinnedItems,
            pinnedTables,
            setPinnedTables,
            pinnedSections,
            setPinnedSections,
            togglePinItem,
            togglePinTable,
            togglePinSection,
            showMenuImages,
            setShowMenuImages,
            posCardLayout,
            setPosCardLayout,
            themeMode,
            setThemeMode,
            checkoutMode,
            setCheckoutMode,
            isOnline,
            offlineOrdersQueue,
            setOfflineOrdersQueue,
            removeOfflineQueueItem,
            clearOfflineQueue,
            deleteOrder,
            syncOfflineOrders,
            syncTenantDataFromDatabase,
            emergencyRecoverStuckOrders,
            quickInsertMissingBill,
            fetchCloudTablesOnly,
            bulkClearTables,
            fetchFreshTablesAndOrders,
            reassignOrderBranch,
            autoFixBranchMismatches,
            scanOrdersAudit,
            applyOrdersFix,
            forceSettleOrder,
            branches,
            setBranches,
            branchSettings,
            setBranchSettings,
            getActiveBranchId,
            generateUniqueOrderId,
            generateUniqueKOTId,
            isSubscriptionBlocked,
            subscriptionLockReason
        }}>
            {children}
            <Dialog
                open={offlineConfirmModal.open}
                onClose={handleCancelOfflineSave}
                PaperProps={{
                    sx: { borderRadius: 3, p: 1, maxWidth: 450 }
                }}
            >
                <DialogTitle sx={{ fontWeight: 800, color: '#d97706', display: 'flex', alignItems: 'center', gap: 1 }}>
                    {t('modal-offline-title')}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ color: '#1f2937', fontWeight: 600, mb: 1 }}>
                        {t('modal-offline-subtitle')}
                    </Typography>
                    {offlineConfirmModal.errorMsg && (
                        <Box sx={{ background: '#fffbebeb', border: '1px solid #fef3c7', p: 1.5, borderRadius: 2, mb: 2 }}>
                            <Typography variant="body2" sx={{ color: '#b45309', fontWeight: 600, fontSize: '0.85rem' }}>
                                {offlineConfirmModal.errorMsg}
                            </Typography>
                        </Box>
                    )}
                    <Typography variant="body2" sx={{ color: '#374151', fontWeight: 600 }}>
                        {t('modal-offline-question')}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
                    <Button
                        onClick={handleCancelOfflineSave}
                        variant="outlined"
                        color="inherit"
                        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
                    >
                        {t('modal-offline-cancel')}
                    </Button>
                    <Button
                        onClick={handleConfirmOfflineSave}
                        variant="contained"
                        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 800, bgcolor: '#d97706', '&:hover': { bgcolor: '#b45309' } }}
                    >
                        {t('modal-offline-confirm')}
                    </Button>
                </DialogActions>
            </Dialog>
        </AppContext.Provider>
    );
};

export const useApp = () => useContext(AppContext);
